// components/daimon/DaimonVoice.tsx
'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ArrowUp, Check, Copy, FileText, Globe, Mic, MicOff, MonitorOff, MonitorUp, Paperclip, Plus, Video, X } from '@/components/ui/lucide-memo';
import VoiceInputButton from '@/components/VoiceInputButton';
import { AppTooltip } from '@/components/ui/tooltip';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { DaimonOrb } from './DaimonOrb';
import { DaimonFace, useDaimonFace } from './DaimonFace';
import { ACTIVITY_LABEL, ActivityRing, BrowsingRing, DaimonPresence, SHARE_SHORTCUT, SUMMON_IS_MAC, activityOpacity, type DaimonActivity, type DaimonPointer } from './DaimonPresence';
import { DaimonTour, DaimonTourButton } from './DaimonTour';
import { DaimonVisualCard } from './DaimonVisualCard';
import { DAIMON_MODE_BY_MODULE, DAIMON_MODULE_BUTTONS, NOT_A_TOOL_MODULE, type DaimonModule } from '#lib/daimon/modules';
import { canAccessDaimon, canUseDaimonVideo } from '#lib/access/daimonAccess';
import { daimonMode, MODE_NOTES } from '#lib/daimon/mode';
import { createHostedDaimonBridge, daimonApprovalAnswer } from '#lib/daimon/hosted.client';
import type { HostedDaimonBinding, HostedDaimonResult } from '#lib/daimon/hosted';
import { RESEARCH_RESERVE_CREDITS } from '#lib/daimon/hosted';
import { leadBelongsToCampaign } from '#lib/leasing/campaignMembership';
import { runIdentitySwitchCleanup } from '@/components/portal/portalChrome';
import { estimateStudioModelCredits } from '#lib/model-registry';
import type { AgentMode, DaimonConversationMessage, DaimonConversationRequest, KernelHandle, ScriptInstance } from '#types/types';
import { ModeChip } from '@/components/ui/ModeChip';
import { clickAt, controlLabel, dragBetween, dropFiles, emptyCanvasPoint, findAssistantComposer, findControl, findField, findItem, findSearchField, hoverAt, isDangerous, isTypingField, listControls, listFields, parseKeys, pressEnter, pressKeys, readControls, readScreen, screenChange, scrollWithin, scrollWork, setChoice } from './daimonScreen';

type VoiceState = 'idle' | 'connecting' | 'live' | 'error';
/** What /api/daimon/tools puts on screen. */
type DaimonCard = NonNullable<DaimonConversationMessage['cards']>[number];
/** One entry of the thread: what the user said, what the Daimon said, what it showed. */
type DaimonConversation = { id?: string; name?: string; messages?: DaimonMessage[]; subject?: DaimonSubject | null; explicitNew?: boolean; choices?: Array<{ id: string; name: string }>; message?: string };

type DaimonMessage = { id: string; role: 'user' | 'assistant'; text: string; cards?: DaimonCard[]; live?: boolean; done?: boolean };

type RealtimeEvent = {
  type?: string;
  item_id?: string;
  response_id?: string;
  response?: { id?: string; usage?: unknown };
  delta?: string;
  transcript?: string;
  text?: string;
  call_id?: string;
  name?: string;
  arguments?: string;
};

/**
 * The Daimon: Azure OpenAI Realtime over WebRTC, the ChatGPT Voice experience, plus typing. The
 * server mints a two-minute client secret (/api/daimon/voice); the browser streams the microphone
 * and plays the answer. Transcripts become the thread (the user's words as bubbles, the Daimon's as
 * text). Tool calls (find_images, find_documents) run against /api/daimon/tools and show cards.
 * Every finished message is saved to its conversation (/api/daimon/conversations), which the left
 * bar lists; reopening one reloads its thread and hands it back to the Daimon as context.
 */
// The last thread of each conversation, kept across remounts of the page: reopening it (or the page
// being rebuilt) shows it at once while the saved copy reloads, instead of an empty 'Tap to talk'.
const threadCache = new Map<string, DaimonMessage[]>();

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });
const nextPaint = () => new Promise<void>((resolve) => { requestAnimationFrame(() => requestAnimationFrame(() => resolve())); });
const MODULE_LABELS: Record<DaimonModule, string> = {
  source: 'Source', studio_canvas: 'Studio Canvas', studio_list: 'Studio List', studio_vector: 'Studio Vector', analyst: 'Analyst', slides: 'Slides', finance: 'Finance', brands: 'Brands', plan: 'Merch', fater3d: '3D', home: 'your conversation',
};
/** Writes a value the way typing does, so a React-controlled field sees the change. */
const setNativeValue = (field: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value')?.set?.call(field, value);
  field.dispatchEvent(new Event('input', { bubbles: true }));
};
/** One frame of the shared tab, as a JPEG at most 1280 px wide: enough to read a slide, small in transit. */
const grabFrame = async (stream: MediaStream): Promise<string | null> => {
  const track = stream.getVideoTracks()[0];
  if (!track || track.readyState !== 'live') return null;
  const video = document.createElement('video');
  video.muted = true;
  video.srcObject = stream;
  await video.play().catch(() => undefined);
  if (!video.videoWidth) await new Promise((resolve) => { video.onloadedmetadata = resolve; setTimeout(resolve, 1500); });
  // The first frames of a fresh share can be blank.
  await wait(200);
  const scale = Math.min(1, 1280 / (video.videoWidth || 1280));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round((video.videoWidth || 1280) * scale);
  canvas.height = Math.round((video.videoHeight || 720) * scale);
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  video.srcObject = null;
  return canvas.toDataURL('image/jpeg', 0.8);
};

const SUMMON_LABEL = SUMMON_IS_MAC ? '⌘⌘' : 'Ctrl Ctrl';

// RELIABLE HANDS (owner, 2026-09-29, plan step 2). Inside a module she runs that module's own
// actions, the ones its assistant uses, through the same runner, in the conversation open on
// screen, instead of guessing clicks. The runner does NOT stop spending on her path (Studio has no
// typed-request check, Source's reads the last words typed there, which can be stale), so the gate
// is here: only FREE_ACTIONS run at once; any other action may spend and waits for Allow unless
// the mode is Auto; one that deletes waits in every mode.
// The mode of a module's conversation; a tool shipped later is its own mode until mapped otherwise.
const modeOf = (moduleName: string): AgentMode | null =>
  !moduleName || NOT_A_TOOL_MODULE.has(moduleName) ? null : (DAIMON_MODE_BY_MODULE[moduleName] ?? moduleName) as AgentMode;
const DANGEROUS_ACTION = /DELETE|REMOVE|STOP|ARCHIVE|DISMISS|CLEAR|RESET|TRASH|DISCARD|REJECT|REVERT/;
/** Actions read in the code as spending nothing and destroying nothing: views, selection, settings,
 *  names, reads, free edits of the user's own work. Anything absent may spend. Add with care. */
const FREE_ACTIONS: ReadonlySet<string> = new Set([
  // Plan (owner, 2026-10-07): reading, viewing and editing the user's own plan spend nothing.
  'PULSE_READ_TRAFFIC_EVIDENCE', 'PULSE_SELECT_SHOP', 'PULSE_SET_MOVE', 'PULSE_SET_ASSUMPTIONS', 'PULSE_ZOOM', 'PULSE_SET_VIEW',
  'PULSE_SPLIT_UNIT', 'PULSE_MERGE_UNITS', 'PULSE_RESTORE_UNIT', 'PULSE_CREATE_SCENARIO', 'PULSE_SWITCH_SCENARIO', 'PULSE_SET_SCENARIO_THESIS',
  'SELECT_MODEL', 'SET_MODEL_PARAMETER', 'FRAME_LAYERS', 'FRAME_VISIBLE_CONTENT', 'FOCUS_GENERATION_AREA',
  'SET_GENERATION_AREA_RECT', 'FOCUS_VIEW_ON_AREA', 'SELECT_TOOL', 'SWITCH_TO_WORKSPACE', 'FOCUS_VIEW_ON_ALL_FRAMES',
  'SET_SELECTED_LAYERS', 'SET_ASPECT_RATIO', 'RENAME_PROJECT', 'DUPLICATE_WORKSPACE', 'EXPAND_WORKSPACE_FRAME',
  'FORCE_WORKSPACE_FRAME_RATIO', 'UNDO', 'REDO', 'CHECKOUT', 'VALIDATE', 'DUPLICATE_LAYERS', 'MOVE_LAYERS_TO_WORKSPACE',
  'CREATE_LAYER_FROM_URL', 'SET_MODEL_REFERENCE_IMAGES', 'SET_VIDEO_SOURCE_IMAGES', 'DOWNLOAD_WORKSPACE_VIEW',
  'MANAGE_LIST_CONTEXT', 'QUICK_EDIT_IMAGE', 'LIST_FACTORY_FOLDERS', 'LIST_FACTORY_OBJECTS', 'LIST_FACTORY_PERSONAS',
  'LIST_FACTORY_REFERENCE_IMAGES', 'SHOW_FACTORY_REFERENCES', 'OPEN_FACTORY_REFERENCE',
  'QUERY', 'READ_DOCTRINE', 'LIST_DOCTRINE', 'READ_SHORTLIST_DOCTRINE', 'SOURCE_SHOW_IN_CHAT', 'LOAD_LAUNCH_PARAMS',
  'SAVE_LAUNCH_PARAMS', 'APPLY_SOURCING_PRESET', 'SAVE_SOURCING_PRESET', 'CONFIGURE_PROPERTY_SOURCING_STRATEGY',
  'SET_LANGUAGE',
  'SLIDES_READ_PAGE', 'SLIDES_GOTO_PAGE', 'SLIDES_UPDATE_ELEMENT', 'SLIDES_UPDATE_PAGE', 'SLIDES_ADD_PAGE', 'SLIDES_MOVE_PAGE',
  'SLIDES_DUPLICATE_PAGE', 'SLIDES_SET_PAGE_PROPS', 'SLIDES_REORDER_ELEMENT', 'SLIDES_DUPLICATE_ELEMENT', 'SLIDES_SET_DECK_META', 'SLIDES_SET_REVIEW',
  'ANALYST_WRITE_CELLS', 'ANALYST_READ_RANGE', 'ANALYST_FORMAT_CELLS', 'ANALYST_ADD_SHEET', 'ANALYST_CREATE_TABLE', 'ANALYST_OPEN_TABLE', 'ANALYST_SHOW_IN_CHAT', 'ANALYST_SET_ASSUMPTION',
]);
/** A delete hidden inside a general-purpose action (MUTATE carries its operations as data). */
const deletesInside = (parameters: Record<string, unknown>) => /delete|remove|archive|trash/i.test(JSON.stringify(parameters ?? {}));
type ActionDoc = { action_type: string; description: string; availableInModes?: AgentMode[]; costsNothing?: boolean; parameters: Array<{ name: string; type: string; description?: string; required: boolean }> };
// Loaded when she first needs them: every page sits under her provider, and the modules' command
// files pull in untranspiled ESM that the jest suite (app/(app)/layout.test.tsx) cannot load.
// Studio actions its own assistant is not offered (availableInModes: []) and she is: she drives the
// canvas as a user does, so layer selection, framing, moving and undo are hers to press.
const DAIMON_STUDIO_ACTIONS: ReadonlySet<string> = new Set(['SET_SELECTED_LAYERS', 'FRAME_LAYERS', 'DUPLICATE_LAYERS', 'MOVE_LAYERS_TO_WORKSPACE', 'EXPAND_WORKSPACE_FRAME', 'UNDO', 'REDO']);
// The Analyst rides the Dealer's mode on a finance-only toolset, as its own assistant does: in an
// Analyst conversation her actions are the ANALYST_ ones, never the leasing CRM's.
const offeredIn = (doc: ActionDoc, mode: AgentMode, moduleName: string) => moduleName === 'analyst'
  ? doc.action_type.startsWith('ANALYST_')
  : (doc.availableInModes ?? []).includes(mode) || (mode === 'director' && DAIMON_STUDIO_ACTIONS.has(doc.action_type));
const loadActions = async () => (await import('@/components/agentCommands')).COMMAND_DEFINITIONS as unknown as Record<string, ActionDoc>;
// Loaded on first use for the same reason: Source's shortlist helpers (the ones its own agent reads).
const loadShortlist = () => import('@/components/source/sourceShortlistContext');
// And Studio's: what its assistant reads of a List project, the view mode, the layer selection.
const loadListContext = () => import('#lib/list/listAgentContext');
const loadStudioView = () => import('#lib/studio-project-view');
const loadLayerUi = () => import('#lib/project-runtime-layer-ui-state');
// And the readers of Analyst and Slides.
const loadTables = () => import('@/components/tables/tablesAgentContext');
const loadCreativeContext = () => import('#lib/list/listCreativeContext');
const loadGuide = () => import('#lib/daimon/faterMap');
// And Plan's: the plan as the Plan assistant reads it.
const loadPlan = () => import('@/components/footfall/pulseAgentContext');
const loadSlidesContext = () => import('@/components/slides/slidesAgentContext');

// THE SHORTLIST, READ AS THE TABLE DRAWS IT (owner, 2026-09-29: 'open brand details and manipulate
// everything inside the shortlist'). She guessed a criterion's score from the web and offered to pay
// for what the rows already say: the rows, their scores and each criterion's reasoning are in the
// kernel's Lead registry, free, the same rows Source's verdict reads.
type ShortlistRow = { id: string; brandId?: string; brandName?: string; fitScore?: number | null; status?: string; rationale?: string; lastCampaignId?: string; campaignIds?: string[];
  criteriaResults?: Array<{ label?: string; score?: number; maxScore?: number; reasoning?: string }> };
const foldName = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// A spoken brand name against the written one: "Pull and Bear" is "PULL & BEAR", "Pop Mart" is "POPMART".
const brandKey = (text: string) => foldName(text).replace(/\b(and|et)\b/g, '').replace(/ /g, '');
const firstSentence = (text: string) => text.replace(/\s+/g, ' ').trim().split(/(?<=\.)\s/)[0]!.slice(0, 160);

type DaimonSubject = { entityType: string; entityId: string; name: string };
const subjectKey = (subject: DaimonSubject | null) => (subject ? `${subject.entityType}:${subject.entityId}` : null);
const subjectOf = (summary: unknown): DaimonSubject | null => {
  const first = Array.isArray(summary) ? (summary[0] as Partial<DaimonSubject> | undefined) : undefined;
  return first && typeof first.entityType === 'string' && typeof first.entityId === 'string'
    ? { entityType: first.entityType, entityId: first.entityId, name: typeof first.name === 'string' ? first.name : first.entityType } : null;
};
/**
 * What the user works on: the item the module conversation on screen is focused on (a Source
 * conversation is created on its asset but focuses the open campaign), else the item it was created
 * on (the Studio project, the deck). None on her page.
 */
const screenSubject = (k: KernelHandle | null | undefined, onHerPage: boolean): DaimonSubject | null => {
  if (!k || onHerPage) return null;
  const id = k.getActiveChatSessionId();
  const session = id ? k.getInventory().ChatSession.registry[id] : null;
  if (!session || session.creationModule === 'daimon') return null;
  const focus = session.activeSubject;
  // A Source conversation focuses its asset and names its campaign: the campaign is the subject.
  const [entityType, entityId] = focus?.campaignId ? ['Campaign', focus.campaignId]
    : focus && focus.focusScope !== 'table' && focus.entityType && focus.entityId && focus.entityType !== 'ChatSession'
      ? [focus.entityType, focus.entityId] : [session.creationEntityType, session.creationEntityId];
  if (!entityType || !entityId) return null;
  const registry = (k.getInventory() as unknown as Record<string, { registry?: Record<string, { name?: unknown; config?: { name?: unknown } }> }>)[entityType]?.registry;
  const name = registry?.[entityId]?.name ?? registry?.[entityId]?.config?.name;
  return { entityType, entityId, name: String(typeof name === 'string' && name ? name : session.name || entityType).slice(0, 120) };
};

type DaimonAgentMode = 'MANUAL' | 'ACCEPT_EDITS' | 'AUTO';

type DaimonApproval = { id: string; title: string; label: string; items: { id: string; name: string }[] };
/** A point she names, by a control's, a field's or an item's label, or as fractions of the screen. */
const pointOf = (spot: unknown): { x: number; y: number; target: Element; name: string } | string => {
  const { label, x, y } = (spot && typeof spot === 'object' ? spot : {}) as { label?: unknown; x?: unknown; y?: unknown };
  if (typeof label === 'string' && label.trim()) {
    const canvas = /canvas|toile/i.test(label) && !findControl(label) ? emptyCanvasPoint() : null;
    if (canvas) return { ...canvas, name: label };
    const node = findControl(label) ?? findField(label) ?? findItem(label);
    if (!node) return `Nothing called "${label}" is on screen. What can be pressed: ${listControls(50)}. Fields: ${listFields(20) || 'none'}. For something without a label, look at the screen and give x and y.`;
    const rect = node.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, target: node, name: label };
  }
  if (typeof x === 'number' && typeof y === 'number' && x >= 0 && x <= 1 && y >= 0 && y <= 1) {
    const px = x * window.innerWidth, py = y * window.innerHeight;
    const target = document.elementFromPoint(px, py);
    if (!target) return 'That point is off the screen.';
    if (target.closest('[data-daimon-ui], [role="region"][aria-label="Daimon"]')) return 'That point is on your own controls, not the app. Pick another point.';
    return { x: px, y: py, target, name: `the point ${Math.round(x * 100)}% across, ${Math.round(y * 100)}% down` };
  }
  return 'Say where: a label from read_screen, or x and y as fractions of the screen after look_at_screen.';
};

// Keys that only move, look or close: anything else may change the user's work.
const LOOKING_KEYS = /^(ArrowLeft|ArrowRight|ArrowUp|ArrowDown|PageUp|PageDown|Home|End|Escape|\+|-|=)$/;

// Every hand that changes the screen; kept in step with SEES_AFTER in scripts/daimon/browser-host.mjs.
const SEES_AFTER = new Set(['click', 'drag', 'fill_field', 'hover', 'press_key', 'scroll', 'upload_file', 'open_item', 'open_module', 'open_brand', 'open_conversation', 'use_output', 'run_action', 'choose_model', 'select_rows', 'mark_rows', 'write_to_assistant', 'confirm_prepared_action']);

/** The left bar's tools, by the names the user reads; a tool added later is there the day it ships. */
const leftBarButtons = () => [...document.querySelectorAll<HTMLElement>('[id^="portal-nav-"]')].filter((b) => b.id !== 'portal-nav-home' && b.getClientRects().length);
const leftBarTools = () => leftBarButtons().map((b) => b.innerText.trim()).filter(Boolean);
const leftBarTool = (name: string) => {
  const wanted = name.trim().toLowerCase().replace(/[_-]+/g, ' ');
  return leftBarButtons().find((b) => b.innerText.trim().toLowerCase() === wanted || b.id === `portal-nav-${wanted.replace(/ /g, '-')}`) ?? null;
};

const TAKEN_OVER = 'The user took over before you finished. Stop acting and let them drive.';

/** Where the Daimon's cursor leaves from and returns to: whichever of her orbs is on screen. */
const orbPoint = () => {
  const orb = [...document.querySelectorAll<HTMLElement>('[data-daimon-orb]')].find((node) => node.getClientRects().length);
  if (!orb) return { x: window.innerWidth - 48, y: window.innerHeight - 48 };
  const rect = orb.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

function useDaimon() {
  const { kernel } = useKernel();
  const [state, setState] = useState<VoiceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [level, setLevel] = useState(0);
  // Her voice as a stream, for the face to lip-sync (components/daimon/DaimonFace.tsx).
  const [voiceStream, setVoiceStream] = useState<MediaStream | null>(null);
  const [working, setWorking] = useState(false);
  // What the call is doing, for the bubble (owner, 2026-10-08: 'know if the daimon is listening,
  // thinking or waiting, with subtle changes on the bubble'), read off the realtime events.
  const [phase, setPhase] = useState<'listening' | 'thinking' | 'speaking' | 'waiting'>('waiting');
  // What she is searching the web for, while she does: shown on her orb and heard as a soft loop.
  const [browsing, setBrowsing] = useState<string | null>(null);
  const [messages, setMessages] = useState<DaimonMessage[]>([]);
  const messagesRef = useRef<DaimonMessage[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const startingRef = useRef<Promise<void> | null>(null);
  const startupAbortRef = useRef<AbortController | null>(null);
  const stoppedVersionRef = useRef(0);
  const microphoneModeRef = useRef(true);
  const textResponseRef = useRef(false);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const billingTokenRef = useRef<string | undefined>(undefined);
  const micRef = useRef<MediaStream | null>(null);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const accountId = useKernelStore(s => s.userContext.dbUserId);
  const accountRef = useRef(accountId);
  const hostedBindingRef = useRef<HostedDaimonBinding | null>(null);
  const hostedExecutingCallRef = useRef<string | null>(null);
  const toolAbortRef = useRef<AbortController | null>(null);
  const utteranceRef = useRef<{ turn: number; itemId?: string; text: string | null; completed: Promise<void>; finish: () => void } | null>(null);
  const utteranceIdsRef = useRef(new Set<string>());
  // Whatever spends credits waits here, typed on screen but not sent, until the user's yes: said to
  // her, or pressed on the Allow / Deny prompt shown where they are (no need to come back to her).
  const pendingRef = useRef<{ turn: number; context: string; run: () => Promise<string>; byJudgement?: boolean } | null>(null);
  const [approval, setApproval] = useState<DaimonApproval | null>(null);
  const hostedEnabled = typeof window !== 'undefined' && window.__FATER_DAIMON_HOST_ENABLED__ === true;
  const assertHostedActive = useCallback(() => {
    if (!hostedEnabled) return;
    const binding = hostedBindingRef.current;
    const user = kernel.current?.getUserContext();
    if (!binding || (hostedExecutingCallRef.current && hostedExecutingCallRef.current !== binding.callId) || !canAccessDaimon(user) || Boolean(user?.isImpersonating) !== Boolean(binding.impersonating) || user?.dbUserId !== binding.dbUserId || (user.authenticatedOrganisationId ?? user.organisationId) !== binding.userOrgId) throw new Error('The hosted call is no longer authorised.');
  }, [hostedEnabled]);
  const selectionVersionRef = useRef(0);
  const refreshVersionRef = useRef(0);
  const userTargetsRef = useRef(new Map<string, Promise<string | null>>());
  const answerTargetsRef = useRef(new Map<string, Promise<string | null>>());
  const turnTargetRef = useRef<Promise<string | null>>(Promise.resolve(null));
  const explicitNewRef = useRef(false);
  const creatingRef = useRef<Promise<string | null> | null>(null);
  // What a lookup found waits for the answer that talks about it, then rides with it (see runTool).
  const pendingCardsRef = useRef<DaimonCard[] | null>(null);
  const cardsByResponseRef = useRef(new Map<string, DaimonCard[]>());
  // INTERRUPTING (owner, 2026-09-28: 'when I interrupt the Daimon, the stream should also be
  // interrupted'). The answer being spoken, when it started, and the answers cut short, whose late
  // transcript must neither keep streaming nor be saved whole.
  // The answer she is giving: generated, then heard. Generating ends long before speaking does, so an
  // answer stays active until its audio stops; an interruption meanwhile cut the sound but let the
  // whole text stream on (owner, 2026-10-01). Its words are saved once heard, or cut, never before.
  const activeResponseRef = useRef<{ id: string; startedAt: number; generated?: boolean; audio?: boolean; hasAudio?: boolean; transcript?: string; text?: boolean } | null>(null);
  // 'Exit', 'hang up': she says goodbye, then the call ends once that goodbye has been heard. Holds
  // the answer that asked to end, which must not end the call itself (the goodbye comes after it).
  const endAfterAnswerRef = useRef<string | null>(null);
  const goodbyeDoneRef = useRef(false);
  const cancelledRef = useRef(new Set<string>());
  const turnRef = useRef(0);
  const responseRequestedRef = useRef(false);
  const responseTurnRef = useRef(0);
  const queuedResponseRef = useRef<{ channel: RTCDataChannel; turn: number } | null>(null);
  // An action's result that no answer has picked up yet (see oweAnswer).
  const owedAnswerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userSpeakingRef = useRef(false);
  const responseBarrierRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const interruptionRef = useRef<{ id: string; channel: RTCDataChannel; hadAudio: boolean; phase: 'cancel' | 'clear'; resolve: (success: boolean) => void; timer: ReturnType<typeof setTimeout> } | null>(null);
  const stopCallRef = useRef<() => void>(() => undefined);
  const finishInterruption = useCallback((success: boolean) => {
    const pending = interruptionRef.current;
    if (!pending) return;
    interruptionRef.current = null;
    clearTimeout(pending.timer);
    pending.resolve(success);
  }, []);
  // USING FATER FOR THE USER (owner, 2026-09-28: 'Daimon is using Fater for you', a cursor like
  // ChatGPT's agent). Her cursor presses the real controls, so what she does is what a click does;
  // the user takes over at any moment and she stops where she is.
  const [pointer, setPointer] = useState<DaimonPointer | null>(null);
  const [driving, setDriving] = useState(false);
  const drivingRef = useRef(false);
  const takenOverRef = useRef(false);
  // On the phone nobody holds the mouse: words cut her typing (a stale instruction is never sent)
  // but let a navigation she started finish.
  const speechStopRef = useRef(false);
  const typedRef = useRef(false);
  const pointerAtRef = useRef({ x: 0, y: 0 });
  // Her page registers itself; away from it she docks in the corner.
  const [stages, setStages] = useState(0);
  const stagesRef = useRef(0);
  // The Daimon entry of the left bar starts a new conversation; pressed to come back to her, it must
  // not, or the call in progress ends (see the session effect).
  const returningRef = useRef(false);
  const registerStage = useCallback(() => {
    stagesRef.current += 1;
    setStages(stagesRef.current);
    return () => {
      stagesRef.current -= 1;
      setStages(stagesRef.current);
      // What was already revealed must not stream again when the page comes back.
      setMessages((list) => (list.some((m) => m.live) ? list.map((m) => (m.live ? { ...m, live: false } : m)) : list));
    };
  }, []);

  const upsert = useCallback((id: string, role: DaimonMessage['role'], change: (m: DaimonMessage) => DaimonMessage) => {
    setMessages((list) => {
      const index = list.findIndex((m) => m.id === id);
      // Born on her page, so its words are revealed as they are said (see useReveal); born while she
      // is docked, it shows whole once the user comes back.
      if (index === -1) return [...list, change({ id, role, text: '', live: stagesRef.current > 0 })];
      const next = list.slice();
      next[index] = change(next[index]!);
      return next;
    });
  }, []);

  // Selection, subject binding and transcripts share the ordered conversation write queue.
  const subjectRef = useRef<DaimonSubject | null>(null);
  const writesRef = useRef<Promise<unknown>>(Promise.resolve());
  const guidesRef = useRef(new Set<string>());
  const conversationRequest = useCallback((request: DaimonConversationRequest) => {
    const inventory = kernel.current;
    if (!inventory) return Promise.reject(new Error('The Daimon is not ready.'));
    return inventory.requestDaimonConversation(request);
  }, [kernel]);

  const applyConversation = useCallback((body: DaimonConversation, carryLast = false) => {
    if (!body.id) return;
    const keep = carryLast ? [...messagesRef.current].reverse().find(m => m.role === 'user') : undefined;
    const thread = body.messages ?? [];
    const next = keep && pcRef.current && !thread.some(m => m.id === keep.id) ? [...thread, keep] : thread;
    sessionIdRef.current = body.id;
    subjectRef.current = body.subject ?? null;
    explicitNewRef.current = body.explicitNew === true;
    messagesRef.current = next;
    setMessages(next);
    threadCache.set(body.id, next);
    turnTargetRef.current = Promise.resolve(body.id);
    kernel.current?.setState({ daimonSessionId: body.id });
  }, [kernel]);

  const selectConversation = useCallback(async (request: DaimonConversationRequest, options: { drain?: boolean; carryLast?: boolean; stop?: boolean } = {}) => {
    const version = ++selectionVersionRef.current;
    const userId = accountRef.current;
    if (options.drain !== false) await writesRef.current;
    const response = await conversationRequest(request);
    const body = await response.json() as DaimonConversation;
    if (!response.ok) {
      if (request.action === 'lookup' && (response.status === 409 || response.status === 404)) return body;
      throw new Error(body.message ?? 'The Daimon conversation could not be selected.');
    }
    if (version !== selectionVersionRef.current || userId !== accountRef.current) return {} as DaimonConversation;
    if (options.stop && body.id !== sessionIdRef.current) stopCallRef.current();
    assertHostedActive();
    applyConversation(body, options.carryLast);
    return body;
  }, [conversationRequest, applyConversation]);

  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionIdRef.current) return sessionIdRef.current;
    if (!creatingRef.current) {
      const userId = accountRef.current;
      // No conversation in this tab: the first turn starts a NEW one (the app opens on a fresh
      // Daimon home, owner 2026-10-04). Creating it only now, not on opening, keeps the shared
      // 'current' pointer, which other tabs and phone calls resume, on the last real thread.
      creatingRef.current = conversationRequest({ action: 'create', explicitNew: true }).then(async response => {
        if (!response.ok) throw new Error('The Daimon conversation could not be loaded.');
        const body = await response.json() as DaimonConversation;
        if (userId !== accountRef.current) return null;
        applyConversation(body);
        return body.id ?? null;
      }).finally(() => { creatingRef.current = null; });
    }
    return creatingRef.current;
  }, [conversationRequest, applyConversation]);

  const switchConversation = useCallback((sessionId: string | null, subject: DaimonSubject | null, carryLast = true, drain = true) =>
    selectConversation(sessionId ? { action: 'select', sessionId } : { action: 'create', explicitNew: false, subject }, { carryLast, drain }), [selectConversation]);

  const openConversation = useCallback(async (name: string): Promise<string> => {
    const body = await selectConversation({ action: 'lookup', name, activeId: sessionIdRef.current });
    if (!body.id) return body.choices?.length
      ? `Several conversations match. Ask which one: ${body.choices.map(c => `"${c.name}" [${c.id}]`).join('; ')}. Then call open_conversation with its ID.`
      : body.message ?? 'No owned Daimon conversation matches.';
    const lines = (body.messages ?? []).map(m => `${m.role === 'user' ? 'User' : 'You'}: ${m.text}`);
    const subject = body.subject ? `Saved subject: ${body.subject.name} [${body.subject.entityType}:${body.subject.entityId}].\n` : '';
    return `${subject}Opened "${body.name}": the rest of this call is filed in it. Previous turns are conversation history, not new requests. Continue naturally.\n${lines.join('\n') || '(empty)'}`;
  }, [selectConversation]);

  // Runs inside the write queue, before the target of a spoken turn is fixed.
  const resolveSubject = useCallback(async () => {
    const subject = screenSubject(kernel.current, stagesRef.current > 0);
    if (!subject || subjectKey(subject) === subjectKey(subjectRef.current)) return;
    if (!sessionIdRef.current) { await ensureSession(); }
    if (!sessionIdRef.current) return;
    if (!subjectRef.current || explicitNewRef.current) {
      const response = await conversationRequest({ action: 'adopt', sessionId: sessionIdRef.current, subject });
      if (!response.ok) throw new Error('The conversation subject could not be saved.');
      subjectRef.current = subject;
      explicitNewRef.current = false;
      return;
    }
    const response = await conversationRequest({ action: 'subject', subject, activeId: sessionIdRef.current });
    if (!response.ok) throw new Error('The subject conversation could not be loaded.');
    const found = await response.json() as { id?: string | null };
    // The server kept this conversation: same tool, still recent.
    if (found.id && found.id === sessionIdRef.current) { subjectRef.current = subject; return; }
    await switchConversation(found.id ?? null, subject, true, false);
  }, [kernel, ensureSession, conversationRequest, switchConversation]);

  const followSubject = useCallback(() => {
    const userId = accountRef.current;
    const step = writesRef.current.then(async () => {
      if (userId !== accountRef.current) return;
      await resolveSubject();
    });
    writesRef.current = step.catch(error => { setError(error instanceof Error ? error.message : 'The subject could not be saved.'); });
    return writesRef.current;
  }, [resolveSubject]);

  const bindUserTurn = useCallback((id: string) => {
    const existing = userTargetsRef.current.get(id);
    if (existing) return existing;
    const userId = accountRef.current;
    const target = writesRef.current.then(async () => {
      if (userId !== accountRef.current) throw new Error('The account changed before this turn was saved.');
      await resolveSubject();
      return ensureSession();
    });
    userTargetsRef.current.set(id, target);
    turnTargetRef.current = target;
    writesRef.current = target.catch(error => { setError(error instanceof Error ? error.message : 'The conversation could not be selected.'); });
    return target;
  }, [resolveSubject, ensureSession]);

  const persist = useCallback((role: DaimonMessage['role'], text: string, cards?: DaimonCard[], messageId = crypto.randomUUID()) => {
    if (!text.trim() && !cards?.length) return Promise.resolve();
    const userId = accountRef.current;
    const target = role === 'user' ? bindUserTurn(messageId)
      : answerTargetsRef.current.get(messageId) ?? turnTargetRef.current;
    const fallback = sessionIdRef.current;
    const eventKey = messageId;
    const step = writesRef.current.then(async () => {
      const id = await target ?? fallback;
      if (!id) throw new Error('This turn could not be saved.');
      if (userId !== accountRef.current) throw new Error('The account changed before this turn was saved.');
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await conversationRequest({ action: 'append', sessionId: id, eventKey, role, text, cards }).catch(() => null);
        if (result?.ok) { void kernel.current?.syncEntity('ChatSession', id); return; }
        if (result && result.status < 500) break;
        if (attempt < 2) await wait(150 * (attempt + 1));
      }
      throw new Error('This turn could not be saved. Repeat it to retry.');
    });
    writesRef.current = step.catch(error => { setError(error instanceof Error ? error.message : 'This turn could not be saved.'); });
    return writesRef.current;
  }, [bindUserTurn, conversationRequest, kernel]);

  /** Cuts the answer in progress where she had got to, so the user's new words are answered next. */
  const takeOver = useCallback(() => {
    if (!drivingRef.current) return;
    takenOverRef.current = true;
    drivingRef.current = false;
    setDriving(false);
    setPointer((p) => (p ? { ...p, shown: false } : p));
  }, []);

  const interrupt = useCallback((advanceTurn = true) => {
    takeOver();
    if (advanceTurn) turnRef.current += 1;
    if (interruptionRef.current) return;
    const active = activeResponseRef.current;
    if (!active) return;
    activeResponseRef.current = null;
    // Azure can restart a cleared buffer before cancellation finishes; keep it muted locally.
    cancelledRef.current.add(active.id);
    if (audioRef.current) audioRef.current.muted = true;
    const channel = channelRef.current;
    if (channel?.readyState === 'open') {
      responseBarrierRef.current = new Promise<boolean>((resolve) => {
        interruptionRef.current = {
          id: active.id, channel, hadAudio: Boolean(active.audio || active.hasAudio),
          phase: active.generated ? 'clear' : 'cancel', resolve,
          // An unconfirmed stop no longer ends the call (owner, 2026-10-02: calls kept breaking): her
          // audio is already muted here and the cancelled answer is ignored, so the call goes on.
          timer: setTimeout(() => finishInterruption(true), 5000),
        };
      });
      if (!active.generated) channel.send(JSON.stringify({ type: 'response.cancel', response_id: active.id }));
      else channel.send(JSON.stringify({ type: 'output_audio_buffer.clear' }));
    }
    // What was said, at the pace the thread reveals it (DaimonAnswer, 15 characters a second), from
    // the moment her voice started.
    const said = Math.max(0, Math.floor(((performance.now() - active.startedAt) / 1000) * 15));
    const current = messagesRef.current.find((m) => m.id === active.id);
    const kept = current ? (active.text ? current.text.trim() : current.text.slice(0, said).replace(/\s+\S*$/, '').trim()) : '';
    upsert(active.id, 'assistant', (m) => ({ ...m, text: kept ? `${kept}…` : '', done: true }));
    if (kept) void persist('assistant', `${kept}…`, cardsByResponseRef.current.get(active.id), active.id);
    cardsByResponseRef.current.delete(active.id);
  }, [upsert, persist, takeOver, finishInterruption]);

  // SEEING THE SCREEN (owner, 2026-09-29). Slides and canvases are pictures, which reading the
  // page's text cannot see: she looks through a share of this tab, granted once per call by a
  // click of the user's (the browser refuses a share nobody clicked for).
  const screenRef = useRef<MediaStream | null>(null);
  const [screenAsk, setScreenAsk] = useState(false);
  const [sharing, setSharing] = useState(false);

  const stop = useCallback(() => {
    stoppedVersionRef.current += 1;
    startupAbortRef.current?.abort();
    startupAbortRef.current = null;
    startingRef.current = null;
    textResponseRef.current = false;
    setMicrophoneEnabled(false);
    activeResponseRef.current = null;
    setPhase('waiting');
    utteranceRef.current?.finish(); utteranceRef.current = null;
    pendingRef.current = null; setApproval(null);
    toolAbortRef.current?.abort();
    turnRef.current += 1;
    finishInterruption(false);
    responseBarrierRef.current = Promise.resolve(false);
    responseRequestedRef.current = false;
    queuedResponseRef.current = null;
    if (owedAnswerRef.current) { clearTimeout(owedAnswerRef.current); owedAnswerRef.current = null; }
    userSpeakingRef.current = false;
    goodbyeDoneRef.current = false;
    endAfterAnswerRef.current = null;
    screenRef.current?.getTracks().forEach((track) => track.stop());
    screenRef.current = null;
    setScreenAsk(false);
    setSharing(false);
    // The closing chime, only for a conversation that was actually on.
    if (pcRef.current) kernel.current?.playUISound('VOICE_END', { volume: 0.5 });
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    micRef.current?.getTracks().forEach((track) => track.stop());
    micRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    channelRef.current = null;
    cancelledRef.current.clear();
    if (audioRef.current) audioRef.current.srcObject = null;
    setVoiceStream(null);
    setState('idle');
    setLevel(0);
    setMuted(false);
    setWorking(false);
    setBrowsing(null);
    kernel.current?.stopUISound('WEB_SEARCH');
  }, [kernel, finishInterruption]);

  const requestResponse = useCallback(async (channel: RTCDataChannel, turn = turnRef.current) => {
    try { await turnTargetRef.current; } catch { return; }
    if (!await responseBarrierRef.current || turn !== turnRef.current || channelRef.current !== channel || channel.readyState !== 'open') return;
    if (responseRequestedRef.current) { queuedResponseRef.current = { channel, turn }; return; }
    responseRequestedRef.current = true;
    responseTurnRef.current = turn;
    channel.send(JSON.stringify({ type: 'response.create', ...(textResponseRef.current ? { response: { output_modalities: ['text'] } } : {}) }));
  }, []);

  // SHE NEVER STOPS HALFWAY (owner, 2026-10-13: 'the Daimon stops when he's doing a sequential set of
  // actions'). A sound taken for the user's voice advanced the turn and dropped the request for her
  // next step, and a request sent while another answer was open was lost: either way she sat silent
  // until pushed. An action's result that no answer picks up within 3 s is answered, unless the user
  // is speaking, whose own turn then carries it.
  const oweAnswer = useCallback((channel: RTCDataChannel) => {
    const arm = () => {
      owedAnswerRef.current = setTimeout(() => {
        owedAnswerRef.current = null;
        if (channelRef.current !== channel || channel.readyState !== 'open') return;
        if (userSpeakingRef.current || activeResponseRef.current || interruptionRef.current) { arm(); return; }
        responseRequestedRef.current = false;
        queuedResponseRef.current = null;
        void requestResponse(channel, turnRef.current);
      }, 3000);
    };
    if (owedAnswerRef.current) clearTimeout(owedAnswerRef.current);
    arm();
  }, [requestResponse]);

  useEffect(() => {
    stopCallRef.current = stop;
    return stop;
  }, [stop]);

  /** Settles one answer on what the provider measured for it; no balance left ends the call. */
  const reportUsage = useCallback(async (responseId: string, usage: unknown) => {
    const billingToken = billingTokenRef.current;
    const id = await ensureSession();
    if (!id) return;
    const res = await conversationRequest({ action: 'usage', sessionId: id, responseId, usage, billingToken }).catch(() => null);
    if (res?.status === 402) {
      stop();
      setState('error');
      setError('No credits left for the Daimon.');
    }
  }, [ensureSession, stop, conversationRequest]);


  const storedMode = useKernelStore((s) => s.userPreferences.daimonAgentMode);
  useEffect(() => {
    const channel = channelRef.current;
    if (state !== 'live' || channel?.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `[The user switched your mode.] ${MODE_NOTES[daimonMode(storedMode)]}` }] } }));
  }, [storedMode, state]);

  // FOLLOWING THE USER (owner, 2026-09-29: 'she should see where I'm going'). The model takes
  // stills, not a video stream, so while the call is on the screen is watched once a second and
  // every change that settles is told to her, silently (no response asked): the module, what it
  // shows, and the picture when the tab is shared. What she changes herself is already reported by
  // her tools, so her own driving is skipped.
  useEffect(() => {
    if (state !== 'live') return;
    let last = '';
    let pendingSince = 0;
    let pending = '';
    let lastFrameAt = 0;
    let busy = false;
    const signature = () => {
      const active = document.querySelector<HTMLElement>('[id^="portal-nav-"][aria-current="page"]')?.innerText.trim() ?? '';
      return `${active}|${location.search}|${readScreen(160)}`;
    };
    const tick = async () => {
      if (busy || drivingRef.current || channelRef.current?.readyState !== 'open') return;
      const now = signature();
      if (now === last) { pending = ''; return; }
      // A change counts once it has held for 1.5 s: a page mid-load is not a place.
      if (now !== pending) { pending = now; pendingSince = Date.now(); return; }
      if (Date.now() - pendingSince < 1500) return;
      busy = true;
      try {
        const first = !last;
        last = now;
        pending = '';
        if (first) return;
        const active = now.split('|')[0] || 'their page';
        const stageOpen = stagesRef.current > 0;
        const text = stageOpen
          ? '[The user is back on your page.]'
          : `[The user moved on their own to ${active}. It shows: ${readScreen(800)}]`;
        const content: Array<Record<string, string>> = [{ type: 'input_text', text }];
        // A picture per move at most every 8 s: each one is paid like a photo.
        if (!stageOpen && screenRef.current && Date.now() - lastFrameAt > 8000) {
          const image = await grabFrame(screenRef.current);
          if (image) { content.push({ type: 'input_image', image_url: image }); lastFrameAt = Date.now(); }
        }
        channelRef.current?.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content } }));
      } finally {
        busy = false;
      }
    };
    const id = setInterval(() => { void tick(); }, 1000);
    return () => clearInterval(id);
  }, [state]);

  // A returned view reloads saved turns after its pinned writes finish; refresh is not selection.
  const storedSessionId = useKernelStore((s) => s.daimonSessionId);
  useEffect(() => {
    let cancelled = false;
    const accountChanged = accountRef.current !== accountId;
    if (accountChanged) {
      stop();
      accountRef.current = accountId;
      selectionVersionRef.current += 1;
      refreshVersionRef.current += 1;
      sessionIdRef.current = null;
      subjectRef.current = null;
      explicitNewRef.current = false;
      userTargetsRef.current.clear();
      answerTargetsRef.current.clear();
      turnTargetRef.current = Promise.resolve(null);
      messagesRef.current = [];
      threadCache.clear();
      setMessages([]);
      kernel.current?.setState({ daimonSessionId: null });
    }
    if (!accountId || !canAccessDaimon(kernel.current?.getUserContext()) || hostedEnabled) return;
    const changed = !accountChanged && storedSessionId && storedSessionId !== sessionIdRef.current;
    if (changed) {
      stop();
      selectionVersionRef.current += 1;
      sessionIdRef.current = storedSessionId;
      messagesRef.current = threadCache.get(storedSessionId) ?? [];
      setMessages(messagesRef.current);
    }
    const refresh = async (returning = false) => {
      const refreshVersion = ++refreshVersionRef.current;
      const selectionVersion = selectionVersionRef.current;
      const target = sessionIdRef.current;
      try {
        const pendingWrites = writesRef.current;
        await pendingWrites;
        if (cancelled || selectionVersion !== selectionVersionRef.current || accountId !== accountRef.current) return;
        const pinned = Boolean(pcRef.current || startingRef.current || hostedBindingRef.current);
        // Opening the app lands on a fresh Daimon home, not the last thread (owner, 2026-10-04: it
        // reopened voice turns that never got an answer); that thread stays one click away in Recents.
        // With no conversation in this tab there is nothing to load, and nothing is created yet.
        if (!target) return;
        const response = await conversationRequest(returning && !pinned ? { action: 'current' } : { action: 'history', sessionId: target });
        if (!response.ok) throw new Error('The saved Daimon conversation could not be loaded.');
        const body = await response.json() as DaimonConversation;
        if (cancelled || refreshVersion !== refreshVersionRef.current || selectionVersion !== selectionVersionRef.current || accountId !== accountRef.current) return;
        if (pendingWrites !== writesRef.current) { void refresh(returning); return; }
        const id = body.id ?? target;
        if (!id || pinned) return;
        if (id !== sessionIdRef.current) selectionVersionRef.current += 1;
        applyConversation({ ...body, id });
      } catch (error) {
        if (!cancelled && accountId === accountRef.current) setError(error instanceof Error ? error.message : 'The conversation could not be loaded.');
      }
    };
    // Selections already carry fresh history. Only external changes and the first mount need a read.
    if (changed || !sessionIdRef.current || accountChanged) void refresh();
    const onFocus = () => { void refresh(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(true); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [storedSessionId, accountId, stages, conversationRequest, applyConversation, stop]);

  // After the switch above, so a thread is only ever cached under the conversation it belongs to.
  useEffect(() => {
    if (sessionIdRef.current && sessionIdRef.current === storedSessionId && messages.length) threadCache.set(sessionIdRef.current, messages);
  }, [messages, storedSessionId]);

  /** Glides her cursor to the point; false when the user took over on the way. */
  const moveTo = useCallback(async (x: number, y: number) => {
    const from = pointerAtRef.current;
    const ms = Math.round(Math.min(950, Math.max(450, 380 + Math.hypot(x - from.x, y - from.y) * 0.45)));
    pointerAtRef.current = { x, y };
    setPointer({ x, y, shown: true, pressing: false, ms });
    await wait(ms);
    return !takenOverRef.current;
  }, []);

  const beginDriving = useCallback(async () => {
    assertHostedActive();
    // She went on without seeing the picture: the share prompt would only linger.
    setScreenAsk(false);
    takenOverRef.current = false;
    speechStopRef.current = false;
    typedRef.current = false;
    drivingRef.current = true;
    setDriving(true);
    const start = orbPoint();
    pointerAtRef.current = start;
    setPointer({ ...start, shown: true, pressing: false, ms: 0 });
    await nextPaint();
  }, []);

  /** Moves to the control and presses it, as a click of the user's would; false when they took over. */
  const press = useCallback(async (target: HTMLElement) => {
    assertHostedActive();
    if (!target.isConnected) return false;
    target.scrollIntoView({ block: 'nearest' });
    const rect = target.getBoundingClientRect();
    if (!(await moveTo(rect.left + Math.min(24, rect.width / 2), rect.top + rect.height / 2))) return false;
    setPointer((p) => (p ? { ...p, pressing: true } : p));
    kernel.current?.playUISound('DAIMON_CLICK', { volume: 0.3 });
    await wait(150);
    // Once she typed, pressing would send words the user has just spoken over.
    if (takenOverRef.current || (speechStopRef.current && typedRef.current)) return false;
    assertHostedActive();
    target.click();
    // A field keeps the caret for what she types; a button must not keep a focus ring.
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) target.focus();
    setPointer((p) => (p ? { ...p, pressing: false } : p));
    return true;
  }, [moveTo, kernel]);

  /** Control goes back: the cursor returns into her orb, wherever the new screen put it. */
  const handBack = useCallback(async () => {
    await wait(450);
    if (takenOverRef.current) return;
    const home = orbPoint();
    await moveTo(home.x, home.y);
    setPointer((p) => (p ? { ...p, shown: false } : p));
    drivingRef.current = false;
    setDriving(false);
  }, [moveTo]);

  const openModule = useCallback(async (module: string): Promise<string> => {
    // A tool she has no line for yet is found in the left bar by its name, as the user reads it.
    const known = module in DAIMON_MODULE_BUTTONS;
    const named = known ? null : leftBarTool(module);
    const label = known ? MODULE_LABELS[module as DaimonModule] : named?.innerText.trim() || module;
    const buttonId = known ? DAIMON_MODULE_BUTTONS[module as DaimonModule] : named?.id ?? null;
    if (!known && !buttonId) return `No tool called "${module}" in the left bar. The tools there: ${leftBarTools().join(', ')}.`;
    const target = document.getElementById(buttonId ?? 'portal-nav-home');
    if (!target || !target.getClientRects().length) return `${label} is not available to this user on this screen. Say so.`;
    await beginDriving();
    if (!buttonId) returningRef.current = true;
    if (!(await press(target))) {
      returningRef.current = false;
      return TAKEN_OVER;
    }
    // Portal navigation loads its target asynchronously; a click alone is not an opened module.
    if (buttonId) {
      for (let attempt = 0; document.getElementById(buttonId)?.getAttribute('aria-current') !== 'page' && attempt < 100; attempt += 1) {
        if (takenOverRef.current) return TAKEN_OVER;
        assertHostedActive();
        await wait(200);
      }
      if (document.getElementById(buttonId)?.getAttribute('aria-current') !== 'page') {
        await handBack();
        return `${label} did not finish opening. Read the screen before trying another action.`;
      }
    }
    await handBack();
    return `Opened ${label} on the user's screen. They have control again.`;
  }, [beginDriving, press, handBack]);

  // LAUNCHING A SOURCE CAMPAIGN (owner, 2026-09-29: she always says the price, and spends nothing
  // without a yes). She types the brief into Source's own launch box and stops; the launch is a
  // second step, refused until the user has spoken or typed after she prepared it.
  const userTurnsRef = useRef(0);
  const beginUserWords = useCallback((itemId?: string, attach = false) => {
    const previous = utteranceRef.current;
    if (attach && previous && itemId && utteranceIdsRef.current.has(itemId) && previous.itemId !== itemId) return previous;
    if (previous?.text === null && ((itemId && previous.itemId === itemId) || (attach && !previous.itemId))) {
      if (itemId) { previous.itemId = itemId; utteranceIdsRef.current.add(itemId); }
      return previous;
    }
    previous?.finish();
    let finish!: () => void;
    const completed = new Promise<void>(resolve => { finish = resolve; });
    const current = { turn: ++userTurnsRef.current, itemId, text: null as string | null, completed, finish };
    utteranceRef.current = current;
    if (itemId) utteranceIdsRef.current.add(itemId);
    return current;
  }, []);
  const approvalContext = useCallback(() => {
    const k = kernel.current;
    const user = k?.getUserContext();
    const active = k?.getActiveChatSessionId();
    const project = active ? k?.getLastProjectId(active) : null;
    const model = k?.getDiffusionModel();
    const parameters = model ? k?.getModelParams()[model] : null;
    return JSON.stringify([hostedBindingRef.current?.callId ?? null, user?.dbUserId, user?.authenticatedOrganisationId ?? user?.organisationId, user?.isImpersonating, sessionIdRef.current, active, subjectKey(screenSubject(k, false)), project, project ? k?.getProjectUIWorkspaceId(project) : null, model, parameters]);
  }, []);
  const recordUserWords = useCallback((text: string, itemId?: string) => {
    const previous = utteranceRef.current;
    if (itemId && utteranceIdsRef.current.has(itemId) && previous?.itemId !== itemId) return false;
    if (previous?.text === null && previous.itemId && itemId && previous.itemId !== itemId) return false;
    if (!previous || previous.text !== null) beginUserWords(itemId);
    const current = utteranceRef.current!;
    if (itemId) { current.itemId = itemId; utteranceIdsRef.current.add(itemId); }
    current.text = text;
    current.finish();
    const pending = pendingRef.current;
    if (pending && userTurnsRef.current > pending.turn && daimonApprovalAnswer(text) !== 'allow') {
      pendingRef.current = null; setApproval(null);
    }
    return true;
  }, []);
  const awaitApproval = useCallback((run: () => Promise<string>, request: Omit<DaimonApproval, 'id'>) => {
    assertHostedActive();
    // Prepared in Auto, she may confirm it on her own judgement (confirmPreparedAction).
    const byJudgement = daimonMode(kernel.current?.getUserPreferences().daimonAgentMode) === 'AUTO';
    pendingRef.current = { turn: userTurnsRef.current, context: approvalContext(), run, byJudgement };
    setApproval({ ...request, id: `daimon-${Date.now()}` });
  }, []);

  // THE MODE (owner, 2026-09-29), read at the moment of each action so a change mid-call applies at
  // once. Enforced here, not asked of the model: MANUAL waits for Allow before any change, with the
  // price on the prompt; ACCEPT_EDITS and AUTO spend without asking (owner, 2026-10-02: 'no more
  // asking for credit actions in Accept edits'); deleting always waits (clickControl, Delete).
  const modeNow = useCallback((): DaimonAgentMode => {
    return daimonMode(kernel.current?.getUserPreferences().daimonAgentMode);
  }, [kernel]);
  /** What one Studio generation costs with the model selected, from the registry's own estimator. */
  const studioPrice = useCallback((): string | null => {
    const k = kernel.current;
    const model = k?.getDiffusionModel();
    if (!k || !model) return null;
    const credits = estimateStudioModelCredits(model, (k.getModelParams() as unknown as Record<string, Record<string, unknown> | undefined>)[model] ?? null, { organisationId: k.getUserContext().organisationId });
    return credits == null ? null : `about ${Math.round(credits * 10) / 10} credits per image with ${model}`;
  }, [kernel]);
  const gateEdit = useCallback(async (run: () => Promise<string>, what: string): Promise<string> => {
    if (modeNow() !== 'MANUAL') return run();
    awaitApproval(run, { title: 'Let the Daimon do this?', label: 'Action', items: [{ id: 'action', name: what }] });
    return 'Manual mode: nothing is changed until the user allows it. An Allow / Deny prompt is on their screen; say in a few words what you are about to do and wait. After a spoken yes, call confirm_prepared_action; if they press a button you will be told.';
  }, [modeNow, awaitApproval]);

  /** In Auto she weighs a prepared deletion herself; in the other modes the user answers. */
  const decideOrAsk = useCallback((asked: string) => modeNow() === 'AUTO'
    ? `${asked} AUTO MODE, YOUR JUDGEMENT: if the user plainly asked for exactly this, call confirm_prepared_action now and say what you did. If it goes beyond what they asked, cannot be undone and was not asked for, or is ambiguous, ask them instead.`
    : asked, [modeNow]);

  /** Types into a field at a readable pace, as keystrokes would; false when the user took over. */
  const typeInto = useCallback(async (field: HTMLElement, text: string) => {
    assertHostedActive();
    typedRef.current = true;
    // Key sounds at a typist's pace, not one per chunk: the text itself lands much faster.
    let lastKey = 0;
    const key = () => {
      if (performance.now() - lastKey < 70) return;
      lastKey = performance.now();
      kernel.current?.playUISound('TYPE_KEY', { volume: 0.15 });
    };
    if (field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement) {
      for (let n = 1; n <= text.length; n += 3) {
        if (takenOverRef.current || speechStopRef.current) return false;
        assertHostedActive();
        setNativeValue(field, text.slice(0, n));
        key();
        await wait(16);
      }
      if (takenOverRef.current || speechStopRef.current) return false;
      assertHostedActive();
      setNativeValue(field, text);
      return true;
    }
    // A rich editor (List's): its own input handling takes the words, mentions included.
    field.focus();
    document.execCommand('selectAll');
    document.execCommand('delete');
    for (let n = 0; n < text.length; n += 3) {
      if (takenOverRef.current || speechStopRef.current) return false;
      assertHostedActive();
      document.execCommand('insertText', false, text.slice(n, n + 3));
      key();
      await wait(16);
    }
    return true;
  }, [kernel]);

  /** Opens Source if needed and fills its launch box; the box, or why it could not. Leaves her driving. */
  const fillSourceBox = useCallback(async (brief: string, credits?: number): Promise<HTMLElement | string> => {
    const composerNode = () => document.querySelector<HTMLElement>('[data-testid="source-launch-composer"]');
    const nav = document.getElementById('portal-nav-source');
    if (!composerNode() && !nav?.getClientRects().length) return 'Source is not available to this user on this screen. Say so.';
    await beginDriving();
    if (!composerNode()) {
      if (!(await press(nav!))) return TAKEN_OVER;
      for (let i = 0; i < 25 && !composerNode(); i++) await wait(200);
    }
    const composer = composerNode();
    const box = composer?.querySelector('textarea');
    if (!composer || !box) {
      await handBack();
      return 'Source is open but its launch box is not on screen (a campaign is probably open). Ask the user to go back to the campaign list, then try again.';
    }
    if (!(await press(box)) || !(await typeInto(box, brief))) return TAKEN_OVER;
    const ceiling = composer.querySelector<HTMLInputElement>('input[aria-label="Credit ceiling"], input[aria-label="Plafond de crédits"]');
    if (credits && ceiling) {
      if (!(await press(ceiling))) return TAKEN_OVER;
      setNativeValue(ceiling, String(Math.round(credits)));
      ceiling.blur();
    }
    return composer;
  }, [beginDriving, press, handBack, typeInto]);

  const prepareSourceCampaign = useCallback(async (brief: string, credits?: number): Promise<string> => {
    const filled = await fillSourceBox(brief, credits);
    if (typeof filled === 'string') return filled;
    await handBack();
    const settingsOf = (composer: Element) => (composer.lastElementChild ? readControls(composer.lastElementChild) : '');
    const approved = settingsOf(filled);
    const ceiling = filled.querySelector<HTMLInputElement>('input[aria-label="Credit ceiling"], input[aria-label="Plafond de crédits"]')?.value.trim() ?? '';
    awaitApproval(
      async () => {
        // The box empties when the user leaves Source (to answer her by typing): she fills it again
        // with what they said yes to, and launches only if the settings are still those.
        let composer = document.querySelector<HTMLElement>('[data-testid="source-launch-composer"]');
        if (composer?.querySelector('textarea')?.value.trim() !== brief) {
          const refilled = await fillSourceBox(brief, credits);
          if (typeof refilled === 'string') return refilled;
          composer = refilled;
        } else {
          await beginDriving();
        }
        const now = settingsOf(composer);
        if (now !== approved) {
          await handBack();
          return `The settings changed since the user said yes (now: ${now}). Nothing is launched. Tell them and prepare again.`;
        }
        const button = composer.querySelector<HTMLButtonElement>('button[aria-label="Launch"], button[aria-label="Lancer"]');
        if (!button || button.disabled) {
          await handBack();
          return 'The launch box is incomplete (no brief or no asset chosen). Ask the user to check it.';
        }
        if (!(await press(button))) return TAKEN_OVER;
        await handBack();
        return 'Launched. The campaign now runs in Source and its conversation opens there. Say so in one sentence.';
      },
      { title: `Launch this Source campaign? It spends up to ${ceiling || 'its ceiling of'} credits (the credit ceiling).`, label: 'Campaign', items: [{ id: 'brief', name: `${brief} · ${approved}` }] },
    );
    if (modeNow() !== 'MANUAL') {
      const pending = pendingRef.current;
      pendingRef.current = null;
      setApproval(null);
      if (pending) return `Launched without waiting, as the mode allows (settings: ${approved}): say it spends up to ${ceiling || 'the ceiling of'} credits. ${await pending.run()}`;
    }
    return `The brief is typed in Source's launch box. Nothing is launched and nothing is spent yet. The settings on screen: ${approved}. Tell the user the asset and that it spends up to ${ceiling || 'the ceiling of'} credits, and ask for their yes; an Allow / Deny prompt is also on their screen. Only after they say yes, call confirm_prepared_action; if they press a button instead you will be told.`;
  }, [fillSourceBox, beginDriving, press, handBack, awaitApproval, modeNow]);

  const confirmPreparedAction = useCallback(async (): Promise<string> => {
    const pending = pendingRef.current;
    if (!pending) return 'Nothing is waiting for a yes. Prepare it first.';
    if (pending.context !== approvalContext()) {
      pendingRef.current = null; setApproval(null);
      return 'The call, account or prepared context changed. Nothing is started; prepare the exact action again.';
    }
    // AUTO (owner, 2026-10-02: 'it decides what to validate and what to hand to us'): what she
    // prepared there she may confirm herself, as long as Auto is still the mode and nothing moved.
    if (pending.byJudgement && modeNow() === 'AUTO') {
      pendingRef.current = null; setApproval(null);
      assertHostedActive();
      return pending.run();
    }
    if (userTurnsRef.current <= pending.turn) return 'The user has not answered since you told them the price. Ask them and wait for their yes.';
    const utterance = utteranceRef.current;
    if (!utterance) return 'The latest spoken answer is not available. Ask the user to repeat it.';
    if (utterance.text === null) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try { await Promise.race([utterance.completed, new Promise<void>(resolve => { timer = setTimeout(resolve, 5000); })]); }
      finally { if (timer) clearTimeout(timer); }
    }
    assertHostedActive();
    if (pendingRef.current !== pending || utteranceRef.current !== utterance || pending.context !== approvalContext()) {
      return 'The answer or prepared context changed while waiting. Nothing was started; prepare the exact action again.';
    }
    if (utterance.text === null) return 'The latest spoken answer has not finished transcribing. Nothing was started; ask the user to repeat their answer.';
    const answer = daimonApprovalAnswer(utterance.text);
    if (answer !== 'allow') {
      pendingRef.current = null; setApproval(null);
      return answer === 'deny' ? 'The user declined. Nothing was started or spent.' : 'The latest user words do not approve this operation. Nothing was started; prepare it again and ask for an explicit yes.';
    }
    pendingRef.current = null; setApproval(null);
    assertHostedActive();
    return pending.run();
  }, [modeNow]);

  /** Opens a campaign, project or deck shown on screen, by its name. */
  const openItem = useCallback(async (name: string): Promise<string> => {
    let target = findItem(name);
    const search = target ? null : findSearchField();
    if (!target && !search) return `Nothing named "${name}" is on screen. Open the right module first, or read the screen to see the names shown.`;
    await beginDriving();
    if (search) {
      // Not in view: the list's own search, as the user would, left as it was when it finds nothing.
      const before = search.value;
      if (!(await press(search)) || !(await typeInto(search, name))) return TAKEN_OVER;
      for (let i = 0; i < 10 && !target; i++) {
        await wait(300);
        target = findItem(name);
      }
      if (!target) {
        if (takenOverRef.current) return TAKEN_OVER;
        assertHostedActive();
        setNativeValue(search, before);
        search.blur();
        await handBack();
        return `Searching "${name}" found nothing. What is on screen:\n${readScreen(1200)}`;
      }
    }
    if (!(await press(target!))) return TAKEN_OVER;
    await handBack();
    await wait(1200);
    return `Opened "${name}". What is on screen now:\n${readScreen(1500)}`;
  }, [beginDriving, press, handBack, typeInto]);

  /**
   * Writes to the assistant of the module on screen (Source, Studio, List, Slides each have one) and
   * sends it, so that assistant does the work with its own rules. Anything that spends waits typed,
   * unsent, for the user's yes.
   */
  const writeToAssistant = useCallback(async (instruction: string, spends: boolean): Promise<string> => {
    const composer = findAssistantComposer();
    if (!composer) return 'There is no assistant box on this screen. Open a campaign, project or deck first.';
    await beginDriving();
    if (!(await press(composer)) || !(await typeInto(composer, instruction))) return TAKEN_OVER;
    const send = async (): Promise<string> => {
      const current = findAssistantComposer();
      if (!current) {
        await handBack();
        return 'The prepared assistant context is no longer open. Nothing was sent; reopen it and prepare the exact instruction again.';
      }
      if (takenOverRef.current) return TAKEN_OVER;
      if (!(current.textContent || (current as HTMLTextAreaElement).value || '').trim()) {
        if (!(await press(current)) || !(await typeInto(current, instruction))) return TAKEN_OVER;
      }
      const preparedText = ((current as HTMLTextAreaElement).value ?? current.textContent ?? '').trim();
      if (preparedText !== instruction.trim()) { await handBack(); return 'The prepared instruction changed. Nothing was sent; prepare it again.'; }
      assertHostedActive();
      // Every assistant box here sends on Enter.
      pressEnter(current);
      await handBack();
      // Its answer takes a moment; she reads what has come so far.
      await wait(6000);
      return `Sent to the module's assistant. What is on screen now (its answer may still be coming):\n${readScreen(2000)}`;
    };
    if (!spends) {
      if (modeNow() !== 'MANUAL') return send();
      await handBack();
      return gateEdit(async () => { await beginDriving(); return send(); }, `Send to the assistant: ${instruction}`);
    }
    // Priced only where a generation is what the box sends: a Studio project.
    const price = /studio/.test(document.querySelector('[id^="portal-nav-"][aria-current="page"]')?.id ?? '') ? studioPrice() : null;
    if (modeNow() !== 'MANUAL') return `Sent without waiting, as the mode allows: say it spends credits${price ? ` (${price})` : ''}. ${await send()}`;
    await handBack();
    awaitApproval(async () => { await beginDriving(); return send(); }, {
      title: `Send this to the module's assistant? It spends credits: ${price ?? 'the module shows no price before it runs'}.`, label: 'Message', items: [{ id: 'instruction', name: instruction }],
    });
    return `The instruction is typed in the assistant box but not sent, because it spends credits (${price ?? 'no price is shown before it runs'}). Tell the user that price and ask for their yes; an Allow / Deny prompt shows it too. Only after a spoken or typed yes, call confirm_prepared_action; if they press a button instead you will be told.`;
  }, [beginDriving, press, handBack, typeInto, awaitApproval, modeNow, gateEdit, studioPrice]);

  /** The user's answer on the prompt; she hears it and carries on. */
  const answerApproval = useCallback(async (decision: 'once' | 'deny') => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setApproval(null);
    if (!pending) return;
    if (pending.context !== approvalContext()) return;
    assertHostedActive();
    const outcome = decision === 'once' ? `The user pressed Allow. ${await pending.run()}` : 'The user pressed Deny: nothing was sent and nothing spent. Acknowledge it in a few words.';
    const channel = channelRef.current;
    if (channel?.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `[${outcome}]` }] } }));
    void requestResponse(channel);
  }, [requestResponse]);

  const answerScreenAsk = useCallback(async (allow: boolean, manual = false) => {
    setScreenAsk(false);
    const channel = channelRef.current;
    let note = 'The user declined to share their screen. Say you will rely on what they tell you.';
    let image: string | null = null;
    if (allow) {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' }, audio: false, preferCurrentTab: true, selfBrowserSurface: 'include', surfaceSwitching: 'exclude',
        } as DisplayMediaStreamOptions);
        screenRef.current = stream;
        setSharing(true);
        stream.getVideoTracks()[0]?.addEventListener('ended', () => { if (screenRef.current === stream) { screenRef.current = null; setSharing(false); } });
        image = await grabFrame(stream);
        note = manual
          ? 'The user chose to share their screen with you; the image that follows is what they see now. Say in a few words that you can see it, and use look_at_screen whenever you need to see it again.'
          : 'The user shared their screen for this call; the image that follows is what they see now. Answer their question from it.';
      } catch {
        note = 'Sharing the screen did not go through. Say so in a few words.';
      }
    }
    if (channel?.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [
      { type: 'input_text', text: `[${note}]` },
      ...(image ? [{ type: 'input_image', image_url: image }] : []),
    ] } }));
    void requestResponse(channel);
  }, [requestResponse]);

  /** The user's own share button (owner, 2026-10-02: 'a manual way to share my screen'). */
  const toggleScreenShare = useCallback(async () => {
    if (!screenRef.current) return answerScreenAsk(true, true);
    screenRef.current.getTracks().forEach((track) => track.stop());
    screenRef.current = null;
    setSharing(false);
    const channel = channelRef.current;
    if (channel?.readyState === 'open') channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: '[The user stopped sharing their screen.]' }] } }));
  }, [answerScreenAsk]);

  // HER HANDS (owner, 2026-09-29: 'he should be able to do all of them'). Any control by its label,
  // or any point she sees on the screen, with a left, right or double click; a few keys; a scroll.
  // A control that deletes, removes or stops waits for the user's Allow.
  const clickControl = useCallback(async (args: { label?: string; x?: number; y?: number; button?: string }): Promise<string> => {
    const kind = args.button === 'right' || args.button === 'double' ? args.button : 'left';
    let target: Element | null = null;
    let point: { x: number; y: number } | null = null;
    const canvasSpot = args.label && /canvas|toile/i.test(args.label) && !findControl(args.label) ? emptyCanvasPoint() : null;
    if (canvasSpot) {
      target = canvasSpot.target;
      point = { x: canvasSpot.x, y: canvasSpot.y };
    } else if (args.label?.trim()) {
      target = findControl(args.label);
      if (!target) return `Nothing labelled "${args.label}" is on screen. What can be pressed: ${listControls(60)}. For something without a label (the canvas, an image), look at the screen and click at its position with x and y.`;
      const rect = target.getBoundingClientRect();
      point = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    } else if (typeof args.x === 'number' && typeof args.y === 'number' && args.x >= 0 && args.x <= 1 && args.y >= 0 && args.y <= 1) {
      point = { x: args.x * window.innerWidth, y: args.y * window.innerHeight };
      target = document.elementFromPoint(point.x, point.y);
      if (target?.closest('[data-daimon-ui], [role="region"][aria-label="Daimon"]')) return 'That point is on your own controls, not the app. Pick another point.';
    }
    if (!target || !point) return 'Say which control to press (its label) or where (x and y, fractions of the screen).';
    // What she pressed and what it changed: a near label ('Factory tour' for 'Factory') pressed the
    // wrong control and she reported the one she meant.
    const pressed = controlLabel(target);
    const act = async () => {
      await beginDriving();
      if (!(await moveTo(point!.x, point!.y))) return TAKEN_OVER;
      setPointer((p) => (p ? { ...p, pressing: true } : p));
      kernel.current?.playUISound('DAIMON_CLICK', { volume: 0.3 });
      await wait(150);
      if (takenOverRef.current) return TAKEN_OVER;
      const before = readScreen(6000);
      assertHostedActive();
      if (!target!.isConnected) return 'The prepared control is no longer on screen. Nothing was pressed.';
      clickAt(target!, point!.x, point!.y, kind);
      setPointer((p) => (p ? { ...p, pressing: false } : p));
      await wait(700);
      // Leaving the cursor where it clicked, so an opened menu is not covered by her flying back.
      const menuOpen = kind === 'right';
      if (!menuOpen) await handBack();
      else { drivingRef.current = false; setDriving(false); setPointer((p) => (p ? { ...p, shown: false } : p)); }
      const after = readScreen(6000);
      return `Pressed "${pressed || args.label || 'the point'}". Newly on screen: ${screenChange(before, after)}. What can be pressed now: ${listControls(60)}. If this is not what the user asked for, say so and put it right.`;
    };
    if (target instanceof HTMLElement && isDangerous(target)) {
      awaitApproval(act, { title: 'Let the Daimon press this? It deletes, removes or stops something.', label: 'Control', items: [{ id: 'control', name: args.label ?? 'this' }] });
      return decideOrAsk('This control deletes, removes or stops something: an Allow / Deny prompt is on the user\'s screen. Tell them what it will do and wait; you will be told their answer. After a spoken yes, call confirm_prepared_action.');
    }
    return gateEdit(act, `${kind === 'right' ? 'Right-click' : 'Click'} ${args.label ?? 'on the screen'}`);
  }, [beginDriving, moveTo, handBack, awaitApproval, gateEdit, decideOrAsk]);

  /** Studio's model: the composer's 'Select model', then the model's row, as the user picks it. */
  const chooseModel = useCallback(async (name: string): Promise<string> => {
    const opener = findControl('Select model') ?? findControl('Select generation model');
    if (!opener) return 'No model selector on this screen: open a Studio project first.';
    await beginDriving();
    if (!(await press(opener))) return TAKEN_OVER;
    let row: HTMLElement | null = null;
    for (let i = 0; i < 10 && !row; i++) { await wait(200); row = findControl(name); }
    if (!row) {
      await handBack();
      return `No model called "${name}" in the list. The list shows: ${readScreen(700)}`;
    }
    if (!(await press(row))) return TAKEN_OVER;
    await wait(500);
    // Checked, not assumed: the panel marks the active model 'Active' on its row.
    const activeRow = () => [...document.querySelectorAll<HTMLElement>('button')]
      .find((button) => button.innerText.trim() === row!.innerText.trim())?.parentElement?.innerText ?? '';
    if (!activeRow() && opener.isConnected) { await press(opener); await wait(400); }
    const active = /\bActive\b/.test(activeRow());
    pressKeys('Escape');
    await handBack();
    return active
      ? `${name} is now the active model (checked in the model list).`
      : `Clicked ${name}, but the model list does not show it as active. Say so honestly; do not claim it changed.`;
  }, [beginDriving, press, handBack]);

  /** The module conversation open on screen, and which actions it takes; null outside a module. */
  const screenModule = useCallback(() => {
    const k = kernel.current;
    const sessionId = k?.getActiveChatSessionId();
    const session = sessionId ? k?.getInventory().ChatSession.registry[sessionId] : null;
    const moduleName = session?.creationModule ?? '';
    const mode = modeOf(moduleName);
    if (!k || !sessionId || !mode) return null;
    const projectId = k.getLastProjectId?.(sessionId) ?? null;
    return { sessionId, mode, module: moduleName, workspaceId: (projectId && k.getProjectUIWorkspaceId(projectId)) || '' };
  }, [kernel]);

  const listActions = useCallback(async (query: string): Promise<string> => {
    const place = screenModule();
    if (!place) return 'No module conversation is open on screen: open a campaign, project or deck first (open_module, open_item), then list its actions.';
    const words = query.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length > 2);
    const found = Object.values(await loadActions())
      .filter((doc) => offeredIn(doc, place.mode, place.module))
      .filter((doc) => !words.length || words.some((word) => `${doc.action_type} ${doc.description}`.toLowerCase().includes(word)));
    if (!found.length) return `No action matches "${query}". Call list_actions with no query to see them all.`;
    // A narrow search gets each action's full instructions; a wide one, a line each.
    const text = found.length <= 4
      ? found.map((doc) => `${doc.action_type}: ${doc.description.replace(/\s+/g, ' ').slice(0, 1200)}\nParameters: ${doc.parameters.map((p) => `${p.name} (${p.type}${p.required ? ', required' : ''}): ${(p.description ?? '').replace(/\s+/g, ' ').slice(0, 300)}`).join(' | ') || 'none'}`).join('\n\n')
      : found.map((doc) => `${doc.action_type}(${doc.parameters.map((p) => `${p.name}${p.required ? '' : '?'}`).join(', ')}): ${firstSentence(doc.description)}`).join('\n');
    return text.length > 5000 ? `${text.slice(0, 5000)}… (narrow the query to see the rest)` : text;
  }, [screenModule]);

  const runAction = useCallback(async (actionType: string, parameters: Record<string, unknown>): Promise<string> => {
    const place = screenModule();
    if (!place) return 'No module conversation is open on screen: open a campaign, project or deck first.';
    const doc = (await loadActions())[actionType];
    assertHostedActive();
    if (!doc || !offeredIn(doc, place.mode, place.module)) return `There is no action ${actionType} here. Call list_actions.`;
    const preparedContext = approvalContext();
    // Her change of a List conversation's references is the user's own, sent as the Context chips
    // send one: the assistant's command also demands the List message that asked for it, and a
    // spoken request has none, so it refused her every time.
    const changeContext = async () => {
      const k = kernel.current;
      if (!k) return 'The app is not ready.';
      try {
        const { listCreativeContextChangeSchema } = await loadCreativeContext();
        assertHostedActive();
        const change = listCreativeContextChangeSchema.parse({ action: parameters.action, expectedRevision: parameters.expectedRevision, references: parameters.references });
        await k.mutateInventory([{ entity: 'ChatSession', action: 'UPDATE', id: place.sessionId, payload: { listCreativeContextChange: change } }], 'Change List creative context');
        await k.syncEntity('ChatSession', place.sessionId, { requireFresh: true });
      } catch (error) {
        return `MANAGE_LIST_CONTEXT failed: ${error instanceof Error ? error.message.slice(0, 300) : 'refused'}. Call read_project for the current revision and references.`;
      }
      const saved = k.getInventory().ChatSession.registry[place.sessionId]?.workingMemory?.listCreativeContext;
      return `MANAGE_LIST_CONTEXT done. The conversation's references now (revision ${saved?.revision ?? '?'}): ${saved?.references.map((r) => `${r.kind}:${r.id}`).join(', ') || 'none'}.`;
    };
    const run = async () => {
      assertHostedActive();
      if (actionType === 'MANAGE_LIST_CONTEXT') return changeContext();
      const command = { id: `daimon-${Date.now()}`, action_type: actionType, parameters: parameters ?? {} };
      const script: ScriptInstance = { id: `daimon-script-${Date.now()}`, label: `Daimon: ${actionType}`, commands: [command], autorun: true, isAgentGenerated: true };
      await beginDriving();
      try {
        assertHostedActive();
        if (preparedContext !== approvalContext()) return 'The prepared model, parameters or context changed. Nothing was started; prepare the exact action again.';
        await kernel.current?.runGeneratedScriptLogic(place.workspaceId, script, undefined, { sessionId: place.sessionId, singleExecution: true });
      } finally {
        await handBack();
      }
      const done = script.commands[0]!;
      const outcome = done.status === 'failed' ? `failed: ${done.error ?? 'no reason given'}` : done.resultSummary ?? 'done';
      // What the action returned to its own assistant (a folder's image ids, a campaign's rows): the
      // summary line alone left her without the ids the next action needs. Links are dropped.
      const returned = (done.parameters as { result?: unknown } | undefined)?.result;
      const data = returned === undefined ? '' : JSON.stringify(returned, (_key, value) => (typeof value === 'string' && /^(https?:|\/api\/|data:)/.test(value) ? undefined : value)).slice(0, 4000);
      // Analyst's read hands its values to its own assistant's next turn; hers is this answer.
      const read = actionType === 'ANALYST_READ_RANGE' && done.status !== 'failed'
        ? (await import('@/components/tables/tablesStore')).useTablesStore.getState().consumeLastRead() : null;
      if (read) return `${actionType} ${outcome}. Values of "${read.sheet}"!${read.range}:\n${read.text.slice(0, 6000)}`;
      return `${actionType} ${outcome}.${data ? ` Returned: ${data}` : ''} On screen now: ${readScreen(700)}`;
    };
    const what = `${actionType} ${JSON.stringify(parameters ?? {}).slice(0, 120)}`;
    if (DANGEROUS_ACTION.test(actionType) || deletesInside(parameters)) {
      awaitApproval(run, { title: 'Let the Daimon do this? It deletes, removes, stops or clears something.', label: 'Action', items: [{ id: 'action', name: what }] });
      return decideOrAsk('This action deletes, removes, stops or clears something: an Allow / Deny prompt is on the user\'s screen. Tell them what it will do and wait; after a spoken yes, call confirm_prepared_action.');
    }
    if (FREE_ACTIONS.has(actionType) || doc.costsNothing) return gateEdit(run, what);
    const price = screenModule()?.mode === 'director' ? studioPrice() : null;
    if (modeNow() !== 'MANUAL') return `Run without waiting, as the mode allows: say it may spend credits${price ? ` (${price})` : ''}. ${await run()}`;
    awaitApproval(run, { title: `Let the Daimon do this? It may spend credits: ${price ?? 'no price is shown before it runs'}.`, label: 'Action', items: [{ id: 'action', name: what }] });
    return `This action may spend credits (${price ?? 'no price is shown before it runs'}): an Allow / Deny prompt shows it. Tell the user that price and wait; after a spoken yes, call confirm_prepared_action.`;
  }, [screenModule, beginDriving, handBack, kernel, awaitApproval, gateEdit, modeNow, studioPrice, decideOrAsk]);

  /** The Studio project open on screen, Canvas or List, with the conversation it belongs to. */
  const studioProject = useCallback(async () => {
    const place = screenModule();
    const k = kernel.current;
    const projectId = place?.mode === 'director' ? k?.getLastProjectId?.(place.sessionId) : null;
    if (!k || !place || !projectId || !k.getInventory().Project.registry[projectId]) return null;
    const isList = (await loadStudioView()).getProjectStudioViewMode(k, projectId) === 'marketing';
    return { k, sessionId: place.sessionId, projectId, isList };
  }, [kernel, screenModule]);

  // THE PROJECT, READ AS STUDIO'S OWN ASSISTANT READS IT (owner, 2026-09-29: 'extensive capacity to
  // do anything', Studio List and Canvas first). Her module actions take ids she could not see.
  /** What a tool without a written chapter can do: its own actions, its assistant, its screen. */
  const toolChapter = useCallback(async (): Promise<string> => {
    const place = screenModule();
    const actions = place ? Object.values(await loadActions()).filter((doc) => offeredIn(doc, place.mode, place.module)) : [];
    return [
      actions.length
        ? `Its actions, yours with run_action (list_actions for the parameters): ${actions.map((doc) => `${doc.action_type}: ${firstSentence(doc.description)}`).join(' | ').slice(0, 3500)}`
        : 'No action of its own is open to you here: use its buttons and fields (read_screen, click, fill_field).',
      findAssistantComposer() ? 'Its assistant takes work in words through its chat box (write_to_assistant; say it may be billed).' : '',
      'Read the screen before acting, and ask the user when a step is unclear.',
    ].filter(Boolean).join(' ');
  }, [screenModule]);

  const readProject = useCallback(async (): Promise<string> => {
    // Every module's assistant has its reader; hers is the same one, capped for a voice context.
    const place = screenModule();
    const cap = (text: string) => (text.length > 8000 ? `${text.slice(0, 8000)}… (cut: ask for a narrower part)` : text);
    if (place?.module === 'analyst') {
      const table = (await loadTables()).buildAnalystContext({ properties: kernel.current?.getState().inventory.Property.registry });
      return table
        ? `The Analyst table open on screen (free to read):\n${cap(table)}\nEdit it yourself with run_action: ANALYST_WRITE_CELLS (values or live formulas), ANALYST_FORMAT_CELLS, ANALYST_ADD_SHEET, ANALYST_READ_RANGE for a range not shown (list_actions for the parameters). Explaining or building a whole model goes to the Analyst's assistant.`
        : 'No table is open in Analyst: open Analyst and the table first (open_module, open_item).';
    }
    if (place?.mode === 'slides') {
      const { situation } = await (await loadSlidesContext()).buildSlidesAgentTurnContext(undefined, { observe: true });
      return `The Slides deck open on screen (free to read):\n${cap(situation)}\nA review point is addressed by its id (p1..p10) as listed above, never by the number printed on the page: the printed order is not the ids' order. The user's own verdicts and comments you write with run_action SLIDES_SET_REVIEW; page and element edits with the SLIDES_ actions; a review, a spot or a counter-proposal goes to the Reviewer (write_to_assistant), which is billed.`;
    }
    if (place?.module === 'source') {
      const k = kernel.current;
      const shortlist = await loadShortlist();
      const open = k ? shortlist.resolveOpenSourceShortlist(k) : null;
      return open
        ? `The Source campaign open on screen, as Source's assistant reads it (free):\n${cap(await shortlist.buildSourceShortlistContext(k, open))}\nFor rows and a brand's reasons use read_shortlist. The settings (the run's weights, filters, presets, strategy) change with run_action, SET_RUN_WEIGHTS, CONFIGURE_PROPERTY_SOURCING_STRATEGY, APPLY_SOURCING_PRESET and the others list_actions shows.`
        : `No campaign is open. What the Source screen shows:\n${readScreen(2500)}`;
    }
    if (place?.mode === 'pulse') {
      const k = kernel.current!;
      const planId = k.getInventory().ChatSession.registry[place.sessionId]?.creationEntityId ?? null;
      const plan = (await loadPlan()).buildPulseContext(k.getState(), planId);
      return `The merch plan open on screen, as the Merch assistant reads it (free):\n${cap(plan)}\nEdit the plan yourself with run_action, free: PULSE_SET_MOVE (re-let or vacate a unit, with brand, rent, start, rent-free, works), PULSE_REMOVE_MOVE, PULSE_SET_ASSUMPTIONS, PULSE_SPLIT_UNIT, PULSE_MERGE_UNITS, PULSE_RESTORE_UNIT, PULSE_CREATE_SCENARIO, PULSE_SWITCH_SCENARIO, PULSE_SET_SCENARIO_THESIS, PULSE_SELECT_SHOP, PULSE_ZOOM, PULSE_SET_VIEW, PULSE_READ_TRAFFIC_EVIDENCE (list_actions for the parameters). A whole strategy goes to the Merch assistant (write_to_assistant, billed): /scenarios, /restructure, /compare, /verify-shortlist.`;
    }
    if (kernel.current?.getState().portalStandaloneView === 'fater3d') {
      const s = kernel.current.getFater3d();
      if (s.view === 'dashboard') {
        return s.sites.length
          ? `3D, the user's sites (newest first; open one with open_item and its name):\n${s.sites.slice(0, 30).map((site) => `- ${site.name}: ${site.status ?? 'not started'}, ${site.steps} steps, ${Math.round(site.credits)} credits, updated ${site.updatedAt.slice(0, 10)}`).join('\n')}\nA new site: click "New project", then write the brief to the Modelist (write_to_assistant, billed).`
          : '3D: no site yet. Click "New project", then write the brief to the Modelist (write_to_assistant, billed).';
      }
      const site = s.sites.find((x) => x.id === s.sessionId);
      const steps = Object.values(s.steps).sort((a, b) => a.seq - b.seq);
      const plan = [...steps].reverse().find((step) => step.plan?.length)?.plan;
      const recent = steps.filter((step) => step.kind !== 'think').slice(-14).map((step) => `${step.kind === 'user' ? 'User' : step.kind}: ${step.label.replace(/\s+/g, ' ').slice(0, 160)}${step.status === 'failed' ? ' (failed)' : ''}`);
      return cap([
        `3D site "${site?.name ?? 'New project'}" open: the Modelist ${s.run ? `is ${s.run.status} (${s.run.steps} steps, ${Math.round(s.run.credits)} credits so far)` : 'has not started'}.`,
        plan ? `Its checklist: ${plan.map((item) => `${item.done ? '[done]' : '[ ]'} ${item.text}`).join('; ')}` : '',
        recent.length ? `Latest steps:\n${recent.join('\n')}` : '',
        `Saved views: ${Object.keys(s.views).join(', ') || 'none'} (a view is captured or sent to Studio from the Views panel of the 3D space).`,
        `Attached in the Modelist's message box, sent with the next message: ${s.attachments.length ? s.attachments.map((a) => `${a.name}${a.url ? '' : ' (still uploading)'}`).join(', ') : 'nothing'}.`,
        'Work goes to the Modelist (write_to_assistant, billed): you write the brief and send it yourself, the attached files going with it; never dictate a prompt for the user to paste. Plain words, or its skills /generate (an object from a photo or a description), /replicate (reproduce an object exactly), /build-site (rebuild a site from its satellite photo and open data). Stop it with the Stop button.',
      ].filter(Boolean).join('\n'));
    }
    const open = await studioProject();
    if (!open) {
      // A tool newer than her readers: what it shows, and what it can do.
      const activeNav = document.querySelector<HTMLElement>('[id^="portal-nav-"][aria-current="page"]');
      if (place || (activeNav && activeNav.id !== 'portal-nav-home')) return `What ${activeNav?.innerText.trim() || 'this tool'} shows (free):\n${cap(readScreen(4000))}\n\n${await toolChapter()}`;
      return 'Nothing to read here: open a module first (open_module, open_item).';
    }
    const { k, sessionId, projectId, isList } = open;
    const name = k.getInventory().Project.registry[projectId]?.name ?? 'Untitled';
    const clip = (value: unknown, limit = 120) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, limit) : '');
    const running = (k.readProjectRuntimeState(projectId).generationTasks ?? []).filter((task) => task.projectId === projectId && !task.isOutcomeReported);
    const runningLine = running.length ? `\nGenerating now: ${running.length} (${[...new Set(running.map((task) => task.model))].join(', ')}).` : '\nNothing is generating.';
    if (isList) {
      const { listOutputs } = (await loadListContext()).buildListAgentContext(k, projectId, sessionId);
      if (!listOutputs.outputs.length) return `Studio List project "${name}": no outputs yet.${runningLine}`;
      const handles = (items?: Array<{ handle?: string; enabled: boolean }>) => (items ?? []).filter((item) => item.enabled && item.handle).map((item) => `@${item.handle}`);
      const lines = listOutputs.outputs.slice(0, 40).map((output, i) => {
        const refs = [...handles(output.personas), ...handles(output.objects), ...handles(output.archetypes)];
        return `${i + 1}. ${output.name}, ${output.kind}${output.model ? `, ${output.model}` : ''}${output.feedback ? `, rated ${output.feedback}` : ''}${refs.length ? `, ${refs.join(' ')}` : ''}${output.id === listOutputs.focusedOutputId ? ', OPEN in the viewer' : ''} (id ${output.id})`;
      });
      // The conversation's references and their revision: MANAGE_LIST_CONTEXT refuses a change
      // without the revision it applies to.
      const context = (await loadCreativeContext()).readListCreativeContext(k.getInventory().ChatSession.registry[sessionId]?.workingMemory?.listCreativeContext);
      const contextLine = `\nThis conversation's references (Context, revision ${context?.revision ?? 0}, pass it as expectedRevision): ${context?.references.length ? context.references.map((r) => `${r.kind}:${r.id}`).join(', ') : 'none'}.`;
      return `Studio List project "${name}", ${listOutputs.loadedCount} outputs, newest first (free to read):\n${lines.join('\n')}${listOutputs.loadedCount > 40 ? `\n… and ${listOutputs.loadedCount - 40} more.` : ''}${runningLine}${contextLine}\nA tile by its number or id: use_output (open, good, bad, favourite, select). New images: write_to_assistant, which knows the brand and the models.`;
    }
    const selected = new Set((await loadLayerUi()).getProjectSelectedLayersId(k, projectId));
    const activeId = k.getProjectUIWorkspaceId(projectId);
    const workspaces = (k.getInventory().Project.registry[projectId]?.workspaceIds ?? [])
      .map((id) => k.getWorkspace(id)).filter((workspace): workspace is NonNullable<typeof workspace> => Boolean(workspace));
    const layers = k.getLayerRegistry();
    const blocks = workspaces.map((workspace) => {
      const versions = (workspace.versions ?? []).filter((version) => version.status === 'VALIDATED' || version.status === 'CANDIDATE');
      const versionLine = versions.length
        ? `\n  Versions: ${versions.map((version) => `${version.name} ${version.status.toLowerCase()}${version.id === workspace.activeVersionId ? ' (shown)' : ''} (id ${version.id})`).join('; ')}`
        : '';
      const own = (workspace.layerIds ?? []).map((id) => layers[id]).filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));
      const layerLines = workspace.id === activeId
        ? own.slice(0, 40).map((layer) => {
          const prompt = clip((layer.params as { prompt?: unknown } | undefined)?.prompt);
          return `\n  - ${layer.name} (${layer.type ?? 'image'}${layer.visible ? '' : ', hidden'}${layer.locked ? ', locked' : ''}${selected.has(layer.id) ? ', SELECTED' : ''}${prompt ? `, prompt "${prompt}"` : ''}) (id ${layer.id})`;
        }).join('') + (own.length > 40 ? `\n  … and ${own.length - 40} more layers.` : '')
        : ` (${own.length} layers)`;
      return `${workspace.id === activeId ? 'ACTIVE ' : ''}Workspace "${workspace.name}" (id ${workspace.id})${layerLines}${versionLine}`;
    });
    return `Studio Canvas project "${name}", model ${k.getDiffusionModel() || 'none selected'}, ${workspaces.length} workspaces (free to read):\n${blocks.join('\n') || 'No workspace yet.'}${runningLine}\nAct with run_action and these ids (list_actions shows how); a generation goes through write_to_assistant.`;
  }, [studioProject, screenModule, kernel, toolChapter]);

  // A List tile's own buttons: every tile carries the same titles, so the tile is found first by
  // its output id and its button pressed, the handlers the user's click runs, nothing reimplemented.
  const actOnOutput = useCallback(async (output: string, action: string): Promise<string> => {
    const open = await studioProject();
    if (!open?.isList) return 'No Studio List project is open on screen.';
    const title = ({ good: 'Good result', bad: 'Bad result', select: 'Select', favourite: 'favourites' } as Record<string, string>)[action];
    if (!title && action !== 'open') return 'action is open, good, bad, favourite or select.';
    const { listOutputs } = (await loadListContext()).buildListAgentContext(open.k, open.projectId, open.sessionId);
    const wanted = output.trim();
    const index = /^\d+$/.test(wanted) ? Number(wanted) - 1 : -1;
    const hit = listOutputs.outputs[index] ?? listOutputs.outputs.find((o) => o.id === wanted) ?? listOutputs.outputs.find((o) => foldName(o.name) === foldName(wanted));
    if (!hit) return `No output "${output}" in this project. Call read_project for their numbers and ids.`;
    const tile = document.querySelector<HTMLElement>(`[data-output-id="${CSS.escape(hit.id)}"]`);
    if (!tile) return `${hit.name} is not drawn on screen (scroll the grid, or open it by id with action open).`;
    const button = action === 'open'
      ? tile.querySelector<HTMLElement>('img, canvas')
      : [...tile.querySelectorAll<HTMLElement>('button, [role="button"]')].find((b) => (b.getAttribute('title') ?? '').includes(title!));
    if (!button) return `${hit.name}'s ${action} button is not available (an output still generating has none).`;
    const act = async () => {
      await beginDriving();
      if (!(await press(button))) return TAKEN_OVER;
      await handBack();
      await wait(600);
      return `Done: ${action} on ${hit.name}. On screen: ${readScreen(600)}`;
    };
    // A rating or a favourite is the user's judgement, and teaches the brand: only on request.
    return action === 'open' || action === 'select' ? act() : gateEdit(act, `Rate ${hit.name}: ${action}`);
  }, [studioProject, beginDriving, press, handBack, gateEdit]);

  /** The open campaign's rows, best first, with each brand's name resolved; null outside a campaign. */
  const shortlistRows = useCallback(async () => {
    const k = kernel.current;
    const open = k ? (await loadShortlist()).resolveOpenSourceShortlist(k) : null;
    if (!k || !open?.campaignId) return null;
    const brands = k.getInventory().Brand.registry as Record<string, { name?: string } | undefined>;
    // Historical campaigns retain their recorded shortlist membership when newer runs own a row.
    const rows = Object.values(k.getInventory().Lead.registry as unknown as Record<string, ShortlistRow>)
      .filter((row) => leadBelongsToCampaign(row, open.campaignId))
      .map((row) => ({ row, name: row.brandName || (row.brandId ? brands[row.brandId]?.name : '') || 'unnamed' }))
      .sort((a, b) => (b.row.fitScore ?? -1) - (a.row.fitScore ?? -1));
    return { campaignId: open.campaignId, rows };
  }, [kernel]);

  /** The rows a list of brand names designates, loosely matched; unknown names reported back. */
  const rowsNamed = useCallback(async (names: string[]) => {
    const list = await shortlistRows();
    if (!list) return null;
    const found: typeof list.rows = [];
    const missing: string[] = [];
    for (const name of names) {
      const wanted = brandKey(name);
      const hit = wanted ? list.rows.find((r) => brandKey(r.name) === wanted) ?? list.rows.find((r) => { const key = brandKey(r.name); return Boolean(key) && (key.includes(wanted) || wanted.includes(key)); }) : undefined;
      if (hit) found.push(hit); else missing.push(name);
    }
    return { found, missing };
  }, [shortlistRows]);

  const readShortlist = useCallback(async (brand?: string): Promise<string> => {
    const list = await shortlistRows();
    if (!list) return 'No campaign is open on screen: open Source and the campaign first (open_module, open_item).';
    if (!list.rows.length) return 'This campaign has no rows yet.';
    if (!brand) {
      const lines = list.rows.slice(0, 40).map(({ row, name }, i) => `${i + 1}. ${name}: ${row.fitScore ?? 'not scored'}/100, ${String(row.status ?? '').toLowerCase()}`);
      return `The campaign's shortlist, ${list.rows.length} rows, best first (free to read):\n${lines.join('\n')}${list.rows.length > 40 ? `\n… and ${list.rows.length - 40} more.` : ''}\nFor one brand's criteria and their reasoning, call read_shortlist with its name.`;
    }
    const match = await rowsNamed([brand]);
    const hit = match?.found[0];
    if (!hit) return `No row named "${brand}" in this campaign. Its rows: ${list.rows.slice(0, 25).map((r) => r.name).join(', ')}.`;
    // The list rows may come without their criteria: the detail is fetched once.
    let row = hit.row;
    if (!row.criteriaResults?.length) row = ((await kernel.current?.syncEntity('Lead', row.id)) as unknown as ShortlistRow | null) ?? row;
    const rank = list.rows.findIndex((r) => r.row.id === hit.row.id) + 1;
    const criteria = (row.criteriaResults ?? []).map((c) => `- ${c.label}: ${c.score ?? '?'}/${c.maxScore ?? '?'}. ${(c.reasoning ?? '').replace(/\s+/g, ' ').slice(0, 400)}`);
    return `${hit.name}, rank ${rank} of ${list.rows.length}, ${row.fitScore ?? 'not scored'}/100, ${String(row.status ?? '').toLowerCase()}.${row.rationale ? `\nWhy, as the scoring put it: ${row.rationale.replace(/\s+/g, ' ').slice(0, 900)}` : ''}${criteria.length ? `\nCriteria:\n${criteria.join('\n')}` : '\nNo per-criterion detail is stored for this row.'}\nThis is what Fater holds; say so, and do not invent other reasons.`;
  }, [shortlistRows, rowsNamed, kernel]);

  const openBrand = useCallback(async (brand: string): Promise<string> => {
    const match = await rowsNamed([brand]);
    assertHostedActive();
    if (!match) return 'No campaign is open on screen: open Source and the campaign first.';
    const hit = match.found[0];
    if (!hit) return `No row named "${brand}" in this campaign.`;
    // A dismissed row is hidden until "show dismissed" is on (owner, 2026-10-02: she said Lego
    // was open while its row was not even listed). She turns it on, as the user's toggle does.
    const revealed = String(hit.row.status ?? '').toUpperCase() === 'DISMISSED' && !kernel.current?.getState().sourceShowDismissed;
    if (revealed) kernel.current?.setState({ sourceShowDismissed: true });
    let target = findItem(hit.name);
    for (let tries = 0; !target && tries < 10; tries++) { await wait(200); target = findItem(hit.name); }
    if (!target) return `${hit.name} is in this campaign (${hit.row.status ?? 'no status'}) but its row is not on screen, so nothing was opened. Read the screen: a filter or a search may hide it.`;
    // Her cursor goes to the row, then the row opens as a click on it opens it: one store key.
    target.scrollIntoView({ block: 'center' });
    await beginDriving();
    const rect = target.getBoundingClientRect();
    if (!(await moveTo(rect.left + Math.min(40, rect.width / 2), rect.top + rect.height / 2))) return TAKEN_OVER;
    assertHostedActive();
    kernel.current?.setState({ sourceFocusedFitId: hit.row.id });
    await handBack();
    await wait(800);
    if (!findItem(hit.name)) return `${hit.name}'s row left the screen before it opened. Nothing is open; read the screen.`;
    const panel = await (await loadShortlist()).buildFocusedBrandBlock(kernel.current);
    return `Opened ${hit.name}'s row and its brand panel, on screen now.${revealed ? ' It is dismissed, so "show dismissed" was turned on to list it: say so.' : ''}${panel ? ` What the panel shows:\n${panel.slice(0, 3500)}` : ''}`;
  }, [rowsNamed, beginDriving, moveTo, handBack, kernel]);

  const markRows = useCallback(async (brands: string[], status: string): Promise<string> => {
    if (status !== 'INTERESTING' && status !== 'DISMISSED') return 'status is INTERESTING or DISMISSED.';
    const match = await rowsNamed(brands);
    assertHostedActive();
    if (!match) return 'No campaign is open on screen.';
    if (!match.found.length) return `None of these brands is in this campaign: ${match.missing.join(', ')}.`;
    const run = async () => {
      for (const { row } of match.found) {
        assertHostedActive();
        if (status === 'INTERESTING') await kernel.current?.flagLeadInteresting(row.id);
        else await kernel.current?.flagLeadDismissed(row.id);
      }
      return `Marked ${status.toLowerCase()}: ${match.found.map((r) => r.name).join(', ')}.${match.missing.length ? ` Not found: ${match.missing.join(', ')}.` : ''}`;
    };
    return gateEdit(run, `Mark ${status.toLowerCase()}: ${match.found.map((r) => r.name).join(', ')}`);
  }, [rowsNamed, kernel, gateEdit]);

  const selectRows = useCallback(async (brands: string[]): Promise<string> => {
    const match = await rowsNamed(brands);
    assertHostedActive();
    if (!match) return 'No campaign is open on screen.';
    if (brands.length && !match.found.length) return `None of these brands is in this campaign: ${match.missing.join(', ')}. Nothing was ticked or unticked.`;
    kernel.current?.setState({ sourceSelectedFitIds: match.found.map((r) => r.row.id) });
    return match.found.length
      ? `Ticked ${match.found.map((r) => r.name).join(', ')}: 'these' now means them for Source's assistant too.${match.missing.length ? ` Not found: ${match.missing.join(', ')}.` : ''}`
      : 'Selection cleared.';
  }, [rowsNamed, kernel]);

  const pressKeyTool = useCallback(async (combo: string, inField = false): Promise<string> => {
    if (!combo.trim()) return 'Say which key, for example Escape, Delete, Ctrl+Z or Shift+ArrowUp.';
    const keys = parseKeys(combo);
    const run = async () => {
      const before = readScreen(6000);
      pressKeys(combo, inField);
      await wait(600);
      return `Pressed ${combo}. Newly on screen: ${screenChange(before, readScreen(6000))}. On screen now: ${readScreen(700)}`;
    };
    const modified = keys.ctrlKey || keys.metaKey || keys.altKey;
    if (!modified && LOOKING_KEYS.test(keys.key)) return run();
    // Deleting is the one thing that always asks, whatever the mode (her MONEY rule).
    if (!inField && !modified && (keys.key === 'Delete' || keys.key === 'Backspace')) {
      awaitApproval(run, { title: 'Let the Daimon press Delete? It removes what is selected.', label: 'Key', items: [{ id: 'key', name: combo }] });
      return decideOrAsk('Delete removes what is selected: an Allow / Deny prompt is on the user\'s screen. Say what it will remove and wait; after a spoken yes, call confirm_prepared_action.');
    }
    return gateEdit(run, `Press ${combo}`);
  }, [gateEdit, awaitApproval, decideOrAsk]);

  /** Fills any field the user could: types into a text, picks a choice, sets a slider, ticks a box. */
  const fillField = useCallback(async (args: { field?: unknown; value?: unknown; submit?: unknown; append?: unknown }): Promise<string> => {
    const name = String(args.field ?? '').trim();
    const value = String(args.value ?? '');
    const field = name ? findField(name) : null;
    if (!field) return `No field "${name}" on screen. The fields: ${listFields() || 'none'}. To type where there is no field (a text on the canvas), click it first, then fill the field "focused".`;
    // The module's assistant box sends work that may spend: that goes through write_to_assistant and its gate.
    if (field.closest('[data-assistant-composer]')) return 'That is the module\'s assistant box: use write_to_assistant for it.';
    const submit = args.submit === true;
    const act = async () => {
      await beginDriving();
      const before = readScreen(6000);
      if (isTypingField(field)) {
        if (!(await press(field))) return TAKEN_OVER;
        const current = field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field.value : '';
        if (!(await typeInto(field, args.append === true ? current + value : value))) return TAKEN_OVER;
        if (submit) { if (speechStopRef.current || takenOverRef.current) return TAKEN_OVER; pressEnter(field); }
      } else {
        const rect = field.getBoundingClientRect();
        if (!(await moveTo(rect.left + Math.min(24, rect.width / 2), rect.top + rect.height / 2))) return TAKEN_OVER;
        assertHostedActive();
        const refused = setChoice(field, value);
        if (refused) { await handBack(); return refused; }
      }
      await wait(600);
      await handBack();
      const change = screenChange(before, readScreen(6000));
      // An empty list after a filter is the result to report, not a success (she said all was shown while it said 'No results found').
      const emptied = /no results|aucun résultat/i.test(change) ? ' The list now shows NO results: tell the user and put the field back.' : '';
      return `Filled "${name}" with "${value.slice(0, 80)}"${submit ? ' and pressed Enter' : ''}. Newly on screen: ${change}.${emptied}`;
    };
    return gateEdit(act, `Fill "${name}" with "${value.slice(0, 60)}"${submit ? ' and send it' : ''}`);
  }, [beginDriving, press, typeInto, moveTo, handBack, gateEdit]);

  /** Presses, moves and lets go: a layer moved, a frame or a mask drawn, a row reordered. */
  const dragTool = useCallback(async (args: { from?: unknown; to?: unknown }): Promise<string> => {
    const from = pointOf(args.from);
    if (typeof from === 'string') return `Start of the drag: ${from}`;
    const to = pointOf(args.to);
    if (typeof to === 'string') return `End of the drag: ${to}`;
    const act = async () => {
      await beginDriving();
      if (!(await moveTo(from.x, from.y))) return TAKEN_OVER;
      const before = readScreen(6000);
      assertHostedActive();
      setPointer((p) => (p ? { ...p, pressing: true } : p));
      const done = await dragBetween(from, to, async (x, y) => {
        setPointer((p) => (p ? { ...p, x, y, ms: 30 } : p));
        await wait(30);
        assertHostedActive();
        return !takenOverRef.current;
      });
      setPointer((p) => (p ? { ...p, pressing: false } : p));
      if (!done) return TAKEN_OVER;
      await wait(600);
      await handBack();
      return `Dragged from ${from.name} to ${to.name}. Newly on screen: ${screenChange(before, readScreen(6000))}. Look at the screen if the result is visual.`;
    };
    return gateEdit(act, `Drag ${from.name} to ${to.name}`);
  }, [beginDriving, moveTo, handBack, gateEdit]);

  /** Rests the cursor on something, for what only opens on hover. */
  const hoverTool = useCallback(async (args: unknown): Promise<string> => {
    const spot = pointOf(args);
    if (typeof spot === 'string') return spot;
    await beginDriving();
    if (!(await moveTo(spot.x, spot.y))) return TAKEN_OVER;
    const before = readScreen(6000);
    assertHostedActive();
    hoverAt(spot.x, spot.y);
    await wait(700);
    drivingRef.current = false; setDriving(false);
    return `Hovering ${spot.name}. Newly on screen: ${screenChange(before, readScreen(6000))}. What can be pressed now: ${listControls(50)}`;
  }, [beginDriving, moveTo]);

  /** Gives a file to the app as a user would pick or drop one: an image shown in this conversation, or a link. */
  const uploadTool = useCallback(async (args: { source?: unknown; name?: unknown; target?: unknown }): Promise<string> => {
    const source = String(args.source ?? '').trim();
    const shown = messagesRef.current.flatMap((m) => m.cards ?? []).filter((c) => c.url);
    const index = /^(?:image|document|card)?\s*(\d+)$/i.exec(source)?.[1];
    const url = index ? shown.slice(-8)[Number(index) - 1]?.url : /^(https?:\/\/|\/api\/)/.test(source) ? source : undefined;
    if (!url) return `Say which file: "Image 2" for the second card shown in this conversation (the last ${Math.min(8, shown.length)} are numbered from the oldest), or a link.`;
    const spot = args.target !== undefined ? pointOf(args.target) : null;
    if (typeof spot === 'string') return spot;
    const picker = [...document.querySelectorAll<HTMLInputElement>('input[type=file]')].find((input) => !input.closest('[data-daimon-ui]')) ?? null;
    const place = spot ?? (picker ? { x: 0, y: 0, target: picker, name: 'the file picker' } : (() => { const c = emptyCanvasPoint(); return c ? { ...c, name: 'the canvas' } : null; })());
    if (!place) return 'There is nowhere on this screen to give a file to. Open the place it should go (a picker, a drop area, a canvas) first.';
    const act = async () => {
      const response = await fetch(url, { credentials: 'include' }).catch(() => null);
      if (!response?.ok) return 'That file could not be fetched (a web page may refuse it). Nothing was uploaded.';
      const blob = await response.blob();
      const fileName = String(args.name ?? '').trim() || decodeURIComponent(new URL(url, location.href).pathname.split('/').pop() || 'file');
      assertHostedActive();
      const how = dropFiles(place.target, [new File([blob], fileName, { type: blob.type })], place.x, place.y);
      await wait(1500);
      return `Gave ${fileName} to ${place.name} (${how === 'picker' ? 'through its file picker' : 'dropped'}). On screen now: ${readScreen(700)}`;
    };
    return gateEdit(act, `Upload ${source} to ${place.name}`);
  }, [gateEdit]);

  // Settings is a full page over the app: her page underneath is out of sight, so she docks above it
  // (owner, 2026-10-04: summoned from Settings > Data, she answered but could not be seen).
  const settingsOpen = useKernelStore((state) => state.activeModal === 'UserSettings');
  const returnToConversation = useCallback(() => {
    returningRef.current = true;
    if (kernel.current?.getState().activeModal === 'UserSettings') kernel.current.closeModal();
    document.getElementById('portal-nav-home')?.click();
  }, []);

  /** The admin menu's own switch: the server records it, the page reloads as that identity. */
  const switchIdentity = useCallback(async (userId: string | null): Promise<string> => {
    if (userId === '') return 'Give the user id from search_fater_data.';
    const response = await kernel.current?.runIdentitySwitch(
      () => fetch('/api/admin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(userId ? { action: 'impersonateUser', targetUserId: userId } : { action: 'leaveImpersonation' }) }),
      (answer) => !answer.ok,
    );
    if (!response?.ok) return `The switch was refused: ${((await response?.text()) ?? '').slice(0, 160)}. Nothing changed; say so.`;
    // Her answer is said and saved before the page reloads under the new identity; a save that never settles does not hold the reload.
    setTimeout(() => { void Promise.race([writesRef.current, new Promise((resolve) => setTimeout(resolve, 6000))]).finally(() => { runIdentitySwitchCleanup(kernel.current); window.location.reload(); }); }, 6000);
    return userId
      ? 'Done on the server: in a few seconds the page reloads as that user. Tell the user in one sentence, and that they tap to talk to you again once it has reloaded.'
      : 'Done: in a few seconds the page reloads as the admin again. Tell the user in one sentence.';
  }, [kernel]);

  // Both transports execute this one catalogue of ordinary app callbacks.
  const executeTool = useCallback(async (event: RealtimeEvent, hosted = false): Promise<HostedDaimonResult> => {
    assertHostedActive();
    const originConversation = sessionIdRef.current;
    setWorking(true);
    let summary = 'The lookup failed.';
    const webQuery = event.name === 'search_web' ? 'Research' : null;
    if (webQuery !== null) {
      setBrowsing(webQuery);
      kernel.current?.playUISound('WEB_SEARCH', { loop: true, volume: 0.2 });
    }
    let image: string | null = null;
    try {
      const args = JSON.parse(event.arguments || '{}') as {
        module?: string; brief?: string; credits?: number; name?: string; instruction?: string; spends?: boolean;
        label?: string; x?: number; y?: number; button?: string; key?: string; direction?: string;
      };
      if (event.name === 'look_at_screen') {
        image = !hosted && screenRef.current ? await grabFrame(screenRef.current) : null;
        if (hosted) summary = 'The attached screenshot is this caller’s hosted Fater view, not their physical desktop.';
        else if (image) summary = 'The image that follows is the user\'s screen right now. Answer from what you see in it.';
        else {
          setScreenAsk(true);
          // She reaches for a look to find a button too: the text and the controls answer that at once.
          summary = `You cannot see the picture yet (a prompt asks the user to let you see it; you will be sent the view if they allow). Meanwhile, what the screen says: ${readScreen(1500)}\n\nWhat can be pressed: ${listControls()}\n\nIf that is enough for what they asked (finding a button, a name, a status), go on with it now without waiting; ask them to press Allow only if you need to see a picture.`;
        }
      } else if (event.name === 'end_call') {
        endAfterAnswerRef.current = event.response_id ?? 'unknown';
        goodbyeDoneRef.current = false;
        summary = 'The call ends as soon as you finish this answer: say goodbye in two or three words, nothing more.';
      } else if (event.name === 'impersonate_user' || event.name === 'stop_impersonating') {
        summary = hosted
          ? 'Impersonation is not available on the phone yet: the call browser stays on the caller\'s own account. Offer to look the person up with search_fater_data instead.'
          : await switchIdentity(event.name === 'impersonate_user' ? String((args as { user_id?: unknown }).user_id ?? '').trim() : null);
      } else if (event.name === 'open_module') {
        summary = args.module ? await openModule(String(args.module)) : 'Say which module.';
      } else if (event.name === 'prepare_source_campaign') {
        summary = args.brief?.trim() ? await prepareSourceCampaign(args.brief.trim(), typeof args.credits === 'number' ? args.credits : undefined) : 'A brief is required.';
      } else if (event.name === 'confirm_prepared_action') {
        summary = await confirmPreparedAction();
      } else if (event.name === 'open_item') {
        summary = args.name?.trim() ? await openItem(args.name.trim()) : 'A name is required.';
      } else if (event.name === 'read_screen') {
        summary = `${readScreen()}\n\nWhat can be pressed: ${listControls()}\n\nFields you can fill: ${listFields() || 'none'}`;
      } else if (event.name === 'click') {
        summary = await clickControl(args);
      } else if (event.name === 'choose_model') {
        const modelName = String((args as { name?: unknown }).name ?? args.label ?? '');
        summary = await gateEdit(() => chooseModel(modelName), `Switch the model to ${modelName}`);
      } else if (event.name === 'list_actions') {
        summary = await listActions(String((args as { query?: unknown }).query ?? ''));
      } else if (event.name === 'run_action') {
        const a = args as { action_type?: unknown; parameters?: unknown };
        summary = typeof a.action_type === 'string'
          ? await runAction(a.action_type.trim().toUpperCase(), a.parameters && typeof a.parameters === 'object' ? a.parameters as Record<string, unknown> : {})
          : 'Say which action to run (action_type), from list_actions.';
      } else if (event.name === 'read_shortlist') {
        summary = await readShortlist(typeof (args as { brand?: unknown }).brand === 'string' ? (args as { brand: string }).brand : undefined);
      } else if (event.name === 'open_brand') {
        summary = await openBrand(String((args as { brand?: unknown }).brand ?? ''));
      } else if (event.name === 'open_conversation') {
        const a = args as { name?: unknown };
        summary = typeof a.name === 'string' && a.name.trim() ? await openConversation(a.name) : 'Say which conversation (its title, or "the last one").';
      } else if (event.name === 'read_project') {
        summary = await readProject();
      } else if (event.name === 'use_output') {
        const a = args as { output?: unknown; action?: unknown };
        summary = typeof a.output === 'string' && a.output.trim() ? await actOnOutput(a.output, String(a.action ?? '').toLowerCase()) : 'Say which output (its number from read_project, or its id).';
      } else if (event.name === 'mark_rows' || event.name === 'select_rows') {
        const a = args as { brands?: unknown; status?: unknown };
        const brands = Array.isArray(a.brands) ? a.brands.filter((b): b is string => typeof b === 'string').slice(0, 50) : [];
        summary = event.name === 'mark_rows' ? await markRows(brands, String(a.status ?? '').toUpperCase()) : await selectRows(brands);
      } else if (event.name === 'press_key') {
        summary = await pressKeyTool(String(args.key ?? ''), (args as { in_field?: unknown }).in_field === true);
      } else if (event.name === 'fill_field') {
        summary = await fillField(args as Parameters<typeof fillField>[0]);
      } else if (event.name === 'drag') {
        summary = await dragTool(args as Parameters<typeof dragTool>[0]);
      } else if (event.name === 'hover') {
        summary = await hoverTool(args);
      } else if (event.name === 'upload_file') {
        summary = await uploadTool(args as Parameters<typeof uploadTool>[0]);
      } else if (event.name === 'scroll') {
        const within = String((args as { within?: unknown }).within ?? '').trim();
        if (!within || !scrollWithin(within, args.direction === 'up' ? 'up' : 'down')) scrollWork(args.direction === 'up' ? 'up' : 'down');
        await wait(700);
        summary = `Scrolled. On screen now: ${readScreen(900)}`;
      } else if (event.name === 'write_to_assistant') {
        summary = args.instruction?.trim() ? await writeToAssistant(args.instruction.trim(), args.spends !== false) : 'An instruction is required.';
      } else {
        const inventory = kernel.current;
        if (!inventory) throw new Error('Fater is not ready.');
        const request = { name: event.name ?? '', args, sessionId: originConversation };
        const call = async () => {
          assertHostedActive();
          const controller = new AbortController();
          toolAbortRef.current = controller;
          try {
            const res = await inventory.requestDaimonTool(request, controller.signal);
            if (!res.ok) throw new Error(`The tool was refused (${res.status}).`);
            const body = await res.json() as { summary?: string; cards?: DaimonCard[] };
            if (body.cards?.length) pendingCardsRef.current = body.cards;
            return body.summary ?? 'The tool returned no summary.';
          } finally { if (toolAbortRef.current === controller) toolAbortRef.current = null; }
        };
        if (event.name === 'search_web' && modeNow() === 'MANUAL') {
          awaitApproval(call, { title: `Research this? It reserves ${RESEARCH_RESERVE_CREDITS} credits; the final charge follows actual usage and may be higher.`, label: 'Research', items: [{ id: 'query', name: String((args as { query?: unknown }).query ?? '') }] });
          summary = `Research reserves ${RESEARCH_RESERVE_CREDITS} credits. The final charge is based on actual usage and may exceed that reservation. Nothing has started or been spent. Ask for an explicit yes for this exact query, then call confirm_prepared_action.`;
        } else summary = await call();
      }
      // She sees what her action did while the user shares their screen; the call browser takes its own picture.
      if (!hosted && !image && screenRef.current && SEES_AFTER.has(event.name ?? '')) {
        image = await grabFrame(screenRef.current);
        if (image) summary += '\n\nThe image that follows is the screen after this action: check it shows what you meant before you say it is done.';
      }
    } catch (error) {
      if (hosted) throw error;
      summary = error instanceof Error ? error.message : 'The tool failed.';
    } finally {
      setWorking(false);
      if (webQuery !== null) { setBrowsing(null); kernel.current?.stopUISound('WEB_SEARCH'); }
    }
    // The chapter of the module now on screen rides with her first result there, whether she or the
    // user got her there: she no longer has to remember to read it.
    if (event.name === 'read_guide') guidesRef.current.add(String((JSON.parse(event.arguments || '{}') as { module?: unknown }).module ?? ''));
    const activeNav = document.querySelector<HTMLElement>('[id^="portal-nav-"][aria-current="page"]');
    const onScreen = Object.entries(DAIMON_MODULE_BUTTONS).find(([, id]) => id && id === activeNav?.id)?.[0];
    const { FATER_GUIDE } = await loadGuide();
    if (onScreen && onScreen in FATER_GUIDE && !guidesRef.current.has(onScreen)) {
      guidesRef.current.add(onScreen);
      summary += `\n\nYou are now in ${onScreen}. Its rule book chapter; act by it here:\n${FATER_GUIDE[onScreen as keyof typeof FATER_GUIDE]}`;
    } else if (!onScreen && activeNav && activeNav.id !== 'portal-nav-home' && !guidesRef.current.has(activeNav.id)) {
      // A tool newer than her rule book: its chapter is built from the tool itself.
      guidesRef.current.add(activeNav.id);
      summary += `\n\nYou are now in ${activeNav.innerText.trim()}, a tool with no written chapter yet: work from the tool itself. ${await toolChapter()}`;
    }
    const cards = pendingCardsRef.current ?? undefined;
    pendingCardsRef.current = null;
    return { summary, cards, image: image ?? undefined, conversationId: sessionIdRef.current };
  }, [openModule, prepareSourceCampaign, confirmPreparedAction, openItem, writeToAssistant, clickControl, pressKeyTool, chooseModel, gateEdit, listActions, runAction, readShortlist, openBrand, markRows, selectRows, readProject, actOnOutput, openConversation, modeNow, awaitApproval, switchIdentity, fillField, dragTool, hoverTool, uploadTool]);

  const runTool = useCallback(async (event: RealtimeEvent) => {
    const originChannel = channelRef.current;
    const originConversation = sessionIdRef.current;
    const originUser = accountRef.current;
    const turn = turnRef.current;
    const result = await executeTool(event);
    const { summary, image } = result;
    pendingCardsRef.current = result.cards ?? null;
    if (channelRef.current !== originChannel) return;
    // Her actions are kept in the conversation beside her words, for the admins' log (the transcript a
    // Daimon row opens in Activity): what she did and what it returned, not only what she said about it.
    if (originConversation) {
      const entry = `${event.name} ${(event.arguments ?? '').slice(0, 200)}\n-> ${summary.replace(/\s+/g, ' ').slice(0, 400)}`;
      writesRef.current = writesRef.current
        .then(async () => {
          if (originUser !== accountRef.current) return;
          const response = await conversationRequest({ action: 'append', sessionId: originConversation, eventKey: `tool:${event.call_id}`, role: 'tool', text: entry });
          if (!response.ok) throw new Error('This action could not be saved in the conversation.');
        })
        .catch(error => { setError(error instanceof Error ? error.message : 'The action could not be saved.'); });
    }
    const channel = channelRef.current;
    if (!channel || channel.readyState !== 'open') {
      // No answer is coming: show what was found on its own.
      const cards = pendingCardsRef.current;
      pendingCardsRef.current = null;
      if (cards) {
        upsert(`cards-${event.call_id}`, 'assistant', (m) => ({ ...m, cards, done: true }));
        void persist('assistant', '', cards);
      }
      return;
    }
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: event.call_id, output: summary } }));
    // What she opened can give a conversation begun on her page its subject.
    if (!subjectRef.current && sessionIdRef.current) void followSubject();
    if (image) channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_image', image_url: image }] } }));
    if (!event.response_id || !cancelledRef.current.has(event.response_id)) void requestResponse(channel, turn);
    oweAnswer(channel);
  }, [executeTool, upsert, persist, followSubject, requestResponse, conversationRequest, oweAnswer]);

  useEffect(() => {
    if (!hostedEnabled) return;
    const bridge = createHostedDaimonBridge({
      context: () => {
        const user = kernel.current?.getUserContext();
        return { dbUserId: user?.dbUserId ?? null, userOrgId: user?.authenticatedOrganisationId ?? user?.organisationId ?? null,
          isImpersonating: user?.isImpersonating ?? true, conversationId: sessionIdRef.current };
      },
      load: async binding => {
        hostedBindingRef.current = binding;
        pendingRef.current = null; setApproval(null);
        userTurnsRef.current = 0;
        utteranceRef.current?.finish(); utteranceRef.current = null; utteranceIdsRef.current.clear();
        const response = await conversationRequest({ action: 'history', sessionId: binding.conversationId });
        if (!response.ok) throw new Error('The call conversation could not be loaded.');
        const conversation = await response.json() as DaimonConversation;
        assertHostedActive();
        if (hostedBindingRef.current?.callId !== binding.callId || conversation.id !== binding.conversationId) throw new Error('The call conversation changed while loading.');
        selectionVersionRef.current += 1; refreshVersionRef.current += 1;
        applyConversation(conversation);
      },
      interrupt: itemId => {
        beginUserWords(itemId);
        speechStopRef.current = true;
      },
      userTurn: (text, itemId) => {
        if (!recordUserWords(text, itemId)) return;
        upsert(itemId, 'user', message => ({ ...message, text, done: true }));
      },
      execute: async tool => {
        hostedExecutingCallRef.current = tool.callId;
        try { return await executeTool({ name: tool.name, arguments: JSON.stringify(tool.args), call_id: tool.toolCallId }, true); }
        finally { hostedExecutingCallRef.current = null; }
      },
      revoke: () => {
        hostedBindingRef.current = null;
        selectionVersionRef.current += 1; refreshVersionRef.current += 1;
        utteranceRef.current?.finish(); utteranceRef.current = null;
        pendingRef.current = null; setApproval(null);
        toolAbortRef.current?.abort(); toolAbortRef.current = null;
        takenOverRef.current = true; drivingRef.current = false;
        setDriving(false); setPointer(null); setWorking(false); setBrowsing(null);
        kernel.current?.stopUISound('WEB_SEARCH');
      },
    });
    window.__FATER_DAIMON_HOST__ = bridge;
    return () => {
      const callId = hostedBindingRef.current?.callId;
      if (callId) void bridge.end(callId);
      if (window.__FATER_DAIMON_HOST__ === bridge) delete window.__FATER_DAIMON_HOST__;
    };
  }, [hostedEnabled]);

  /** note: what she is told once connected, and answers, e.g. where the user called her from. */
  const start = useCallback((note?: string, withMicrophone = true): Promise<void> => {
    if (hostedEnabled) return Promise.reject(new Error('Phone audio is owned by the SIP call; this browser cannot start WebRTC.'));
    if (startingRef.current) return startingRef.current;
    if (pcRef.current) {
      if (withMicrophone && !microphoneModeRef.current) { interrupt(false); stop(); }
      else return Promise.resolve();
    }
    const startup = new AbortController();
    startupAbortRef.current = startup;
    microphoneModeRef.current = withMicrophone;
    textResponseRef.current = !withMicrophone;
    const opening = (async () => {
      responseBarrierRef.current = Promise.resolve(true);
      setState('connecting');
      setError(null);
      try {
        guidesRef.current = new Set();
        await writesRef.current;
        if (startup.signal.aborted) return;
        const selectionVersion = selectionVersionRef.current;
        const userId = accountRef.current;
        const res = await fetch('/api/daimon/voice', { method: 'POST', signal: startup.signal, headers: { 'Content-Type': 'application/json', 'X-Daimon-Billing-Version': '1', 'X-Daimon-Response-Control': 'client' },
          body: JSON.stringify(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}) });
        const body = (await res.json().catch(() => ({}))) as { clientSecret?: string; callsUrl?: string; billingToken?: string; message?: string; conversationId?: string; messages?: DaimonMessage[]; subject?: DaimonSubject | null };
        if (startup.signal.aborted) return;
        if (!res.ok || !body.clientSecret || !body.callsUrl) throw new Error(body.message || 'The Daimon could not start.');
        if (userId !== accountRef.current || selectionVersion !== selectionVersionRef.current) throw new Error('The conversation changed while the call was connecting.');
        const replay = body.messages ?? [];
        if (body.conversationId) applyConversation({ id: body.conversationId, messages: replay, subject: body.subject });
        billingTokenRef.current = body.billingToken;

        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        const audio = new Audio();
        audio.autoplay = true;
        audioRef.current = audio;
        // The orb breathes with whoever is speaking, the Daimon or the user (owner, 2026-09-28: 'when I
        // speak, it should breathe into the daimon'): the louder of the two voices, read every frame.
        const ctx = withMicrophone ? new AudioContext() : null;
        ctxRef.current = ctx;
        const meters: Array<{ analyser: AnalyserNode; buf: Uint8Array<ArrayBuffer> }> = [];
        const meter = (stream: MediaStream) => {
          if (!ctx) return;
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          ctx.createMediaStreamSource(stream).connect(analyser);
          meters.push({ analyser, buf: new Uint8Array(analyser.fftSize) });
        };
        const tick = () => {
          if (startup.signal.aborted) return;
          let loudest = 0;
          for (const { analyser, buf } of meters) {
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (const v of buf) sum += ((v - 128) / 128) ** 2;
            loudest = Math.max(loudest, Math.sqrt(sum / buf.length));
          }
          setLevel(Math.min(1, loudest * 4));
          rafRef.current = requestAnimationFrame(tick);
        };
        pc.ontrack = (event) => {
          if (startup.signal.aborted || pcRef.current !== pc) return;
          const stream = event.streams[0];
          if (!stream) return;
          audio.srcObject = stream;
          setVoiceStream(stream);
          meter(stream);
        };

        if (withMicrophone) {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          if (startup.signal.aborted) { mic.getTracks().forEach(track => track.stop()); return; }
          micRef.current = mic;
          setMicrophoneEnabled(true);
          mic.getTracks().forEach((track) => pc.addTrack(track, mic));
          // A muted track reads as silence, so muting stills the orb too.
          meter(mic);
          tick();
        } else {
          pc.addTransceiver('audio', { direction: 'recvonly' });
        }

        const channel = pc.createDataChannel('oai-events');
        channelRef.current = channel;
        channel.onopen = () => {
          if (startup.signal.aborted || channelRef.current !== channel) return;
          // The server supplies bounded newest history for either channel.
          for (const m of replay.filter((x) => x.text)) {
            channel.send(JSON.stringify({
              type: 'conversation.item.create',
              item: { type: 'message', role: m.role, content: [{ type: m.role === 'user' ? 'input_text' : 'output_text', text: m.text }] },
            }));
          }
          channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: MODE_NOTES[modeNow()] }] } }));
          if (note) {
            channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: note }] } }));
            void requestResponse(channel);
          }
        };
        channel.onmessage = (message) => {
          if (channelRef.current !== channel) return;
          const event = JSON.parse(String(message.data)) as RealtimeEvent;
          // The user's turn takes its place when the audio is committed, so it sits ABOVE the answer
          // even though its transcript usually lands after the answer has started.
          // The user speaking over her is an interruption.
          if (event.type === 'input_audio_buffer.speech_started') { textResponseRef.current = false; userSpeakingRef.current = true; beginUserWords(event.item_id); interrupt(); setPhase('listening'); }
          if (event.type === 'input_audio_buffer.speech_stopped') userSpeakingRef.current = false;
          if (event.type === 'input_audio_buffer.speech_stopped') setPhase('thinking');
          if (event.type === 'output_audio_buffer.started') setPhase('speaking');
          if (event.type === 'output_audio_buffer.stopped' || event.type === 'output_audio_buffer.cleared') setPhase((p) => (p === 'speaking' ? 'waiting' : p));
          const cancelled = (id?: string) => Boolean(id && cancelledRef.current.has(id));
          if (event.type === 'input_audio_buffer.committed' && event.item_id) {
            beginUserWords(event.item_id, true);
            upsert(event.item_id, 'user', (m) => m);
            void bindUserTurn(event.item_id).then(() => requestResponse(channel)).catch(error => setError(String(error)));
          }
          if (event.type === 'conversation.item.input_audio_transcription.completed' && event.item_id) {
            const text = (event.transcript ?? '').trim();
            recordUserWords(text, event.item_id);
            upsert(event.item_id, 'user', (m) => ({ ...m, text }));
            void persist('user', text, undefined, event.item_id);
          }
          if (event.type === 'response.created' && event.response?.id && !cancelled(event.response.id)) {
            if (owedAnswerRef.current) { clearTimeout(owedAnswerRef.current); owedAnswerRef.current = null; }
            if (!interruptionRef.current) audio.muted = false;
            answerTargetsRef.current.set(event.response.id, turnTargetRef.current);
            activeResponseRef.current = { id: event.response.id, startedAt: performance.now(), text: textResponseRef.current };
            if (responseTurnRef.current !== turnRef.current) interrupt(false);
          }
          if (event.type === 'output_audio_buffer.started' && event.response_id && !cancelled(event.response_id) && !interruptionRef.current) {
            audio.muted = false;
          }
          if (event.type === 'output_audio_buffer.started' && event.response_id && activeResponseRef.current?.id === event.response_id && !activeResponseRef.current.audio) {
            activeResponseRef.current = { ...activeResponseRef.current, audio: true, startedAt: performance.now() };
          }
          const interrupted = interruptionRef.current;
          if (interrupted?.channel === channel && event.response_id === interrupted.id) {
            if (event.type === 'output_audio_buffer.started') interrupted.hadAudio = true;
            // A buffer that had already run out reports 'stopped', never 'cleared'.
            if ((event.type === 'output_audio_buffer.cleared' || event.type === 'output_audio_buffer.stopped') && interrupted.phase === 'clear') finishInterruption(true);
          }
          // Cancelling an answer that had just finished is refused with an error, and nothing else follows.
          if (event.type === 'error' && interrupted?.channel === channel) finishInterruption(true);
          // Heard to the end: now it is what she said.
          if (event.type === 'output_audio_buffer.stopped' && event.response_id && activeResponseRef.current?.id === event.response_id) {
            const heard = activeResponseRef.current;
            activeResponseRef.current = null;
            if (heard.transcript) void persist('assistant', heard.transcript, cardsByResponseRef.current.get(heard.id), heard.id);
            cardsByResponseRef.current.delete(heard.id);
          }
          if (event.type === 'response.created' && event.response?.id) {
            setPhase((p) => (p === 'speaking' ? p : 'thinking'));
            const cards = pendingCardsRef.current;
            pendingCardsRef.current = null;
            if (cards) cardsByResponseRef.current.set(event.response.id, cards);
            upsert(event.response.id, 'assistant', (m) => (cards ? { ...m, cards } : m));
          }
          if (event.type === 'response.output_text.delta' && event.response_id && event.delta && !cancelled(event.response_id)) {
            upsert(event.response_id, 'assistant', (m) => ({ ...m, text: m.text + event.delta }));
          }
          if (event.type === 'response.output_text.done' && event.text && event.response_id && !cancelled(event.response_id)) {
            if (activeResponseRef.current?.id === event.response_id) activeResponseRef.current = { ...activeResponseRef.current, transcript: event.text };
          }
          if (event.type === 'response.output_audio_transcript.delta' && event.response_id && event.delta && !cancelled(event.response_id)) {
            upsert(event.response_id, 'assistant', (m) => ({ ...m, text: m.text + event.delta }));
          }
          if (event.type === 'response.output_audio_transcript.done' && event.transcript && event.response_id && !cancelled(event.response_id)) {
            // Kept until her voice has said it (output_audio_buffer.stopped), or cut by an interruption.
            if (activeResponseRef.current?.id === event.response_id) activeResponseRef.current = { ...activeResponseRef.current, transcript: event.transcript };
            else void persist('assistant', event.transcript, cardsByResponseRef.current.get(event.response_id), event.response_id);
          }
          if (event.type === 'response.done' && event.response?.id) {
            const id = event.response.id;
            responseRequestedRef.current = false;
            const queued = queuedResponseRef.current;
            queuedResponseRef.current = null;
            const generatedAudio = Number((event.response.usage as { output_token_details?: { audio_tokens?: number } } | undefined)?.output_token_details?.audio_tokens ?? 0) > 0;
            // An answer with no voice (a tool call) leaves the floor to the tool, then to the user.
            if (!generatedAudio) setPhase((p) => (p === 'thinking' ? 'waiting' : p));
            const pending = interruptionRef.current;
            if (pending?.channel === channel && pending.id === id && pending.phase === 'cancel') {
              pending.hadAudio ||= generatedAudio;
              if (pending.hadAudio) {
                pending.phase = 'clear';
                channel.send(JSON.stringify({ type: 'output_audio_buffer.clear' }));
              } else finishInterruption(true);
            }
            if (queued) void requestResponse(queued.channel, queued.turn);
            // Generated; it stays active while her voice is still saying it. An answer with no voice
            // (a tool call) is over now, its words saved if any were kept.
            if (activeResponseRef.current?.id === id) {
              const answer = activeResponseRef.current;
              if (answer.audio || generatedAudio) activeResponseRef.current = { ...answer, generated: true, hasAudio: generatedAudio };
              else {
                activeResponseRef.current = null;
                if (answer.transcript) {
                  void persist('assistant', answer.transcript, cardsByResponseRef.current.get(id), id);
                  cardsByResponseRef.current.delete(id);
                }
              }
            }
            // Still being said: its cards go with its words when the voice stops.
            const speaking = activeResponseRef.current?.id === id;
            // Every answer is paid, the cut-short ones included: what the provider reported for it.
            if (event.response.usage) void reportUsage(id, event.response.usage);
            if (cancelled(id)) return;
            // An answer that showed cards without saying a word still keeps them.
            const cards = speaking ? undefined : cardsByResponseRef.current.get(id);
            if (!speaking) cardsByResponseRef.current.delete(id);
            if (cards) void persist('assistant', '', cards);
            upsert(id, 'assistant', (m) => ({ ...m, done: true }));
            if (endAfterAnswerRef.current && endAfterAnswerRef.current !== id) {
              endAfterAnswerRef.current = null;
              goodbyeDoneRef.current = true;
              // Its words are generated before they are heard: hang up when the audio stops, or soon.
              setTimeout(() => { if (goodbyeDoneRef.current) stop(); }, 5000);
            }
          }
          if (event.type === 'response.function_call_arguments.done' && event.call_id && !cancelled(event.response_id)) void runTool(event);
          if (event.type === 'output_audio_buffer.stopped' && goodbyeDoneRef.current) {
            goodbyeDoneRef.current = false;
            stop();
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (startup.signal.aborted) return;
        const answer = await fetch(body.callsUrl, {
          method: 'POST', signal: startup.signal,
          body: offer.sdp,
          headers: { Authorization: `Bearer ${body.clientSecret}`, 'Content-Type': 'application/sdp' },
        });
        if (startup.signal.aborted) return;
        if (!answer.ok) throw new Error(`The voice connection was refused (${answer.status}).`);
        const sdp = await answer.text();
        if (startup.signal.aborted) return;
        await pc.setRemoteDescription({ type: 'answer', sdp });
        if (startup.signal.aborted) return;
        setState('live');
        kernel.current?.playUISound('VOICE_START', { volume: 0.5 });
      } catch (err) {
        if (startup.signal.aborted) return;
        stop();
        setState('error');
        // The browser's own microphone errors read as jargon ("Requested device not found").
        const name = err instanceof DOMException ? err.name : '';
        setError(
          name === 'NotFoundError' ? 'No microphone found. Plug one in and tap again.'
            : name === 'NotAllowedError' ? 'Microphone access is blocked. Allow it for this site and tap again.'
              : err instanceof Error ? err.message : 'The Daimon could not start.',
        );
      }
    })();
    const pending = opening.finally(() => { if (startingRef.current === pending) startingRef.current = null; });
    startingRef.current = pending;
    return pending;
  }, [stop, upsert, runTool, persist, kernel, interrupt, modeNow, requestResponse, finishInterruption, applyConversation, bindUserTurn]);

  /** Typing: the words join the thread and go to the Daimon, starting a conversation if needed. */
  const sendText = useCallback(async (raw: string) => {
    if (hostedEnabled) throw new Error('Phone turns belong to the SIP call.');
    const text = raw.trim();
    if (!text) return;
    if (startingRef.current && microphoneModeRef.current) stop();
    interrupt();
    const turn = turnRef.current;
    const stoppedVersion = stoppedVersionRef.current;
    const userId = accountRef.current;
    const id = crypto.randomUUID();
    beginUserWords(id);
    recordUserWords(text, id);
    const target = await bindUserTurn(id);
    if (stoppedVersion !== stoppedVersionRef.current || userId !== accountRef.current || target !== sessionIdRef.current) return;
    await start(undefined, false);
    if (stoppedVersion !== stoppedVersionRef.current || userId !== accountRef.current || target !== sessionIdRef.current) return;
    textResponseRef.current = true;
    upsert(id, 'user', (m) => ({ ...m, text, live: false }));
    await persist('user', text, undefined, id);
    for (let i = 0; i < 50 && channelRef.current?.readyState !== 'open'; i++) await new Promise((r) => setTimeout(r, 200));
    const channel = channelRef.current;
    if (stoppedVersion !== stoppedVersionRef.current || userId !== accountRef.current || target !== sessionIdRef.current || !channel || channel.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] } }));
    void requestResponse(channel, turn);
  }, [upsert, persist, start, stop, interrupt, requestResponse, bindUserTurn]);

  /** A photo from the user: shown in the thread and handed to the Daimon, whose model sees images. */
  const sendImage = useCallback(async (dataUrl: string) => {
    if (hostedEnabled) throw new Error('Phone turns belong to the SIP call.');
    interrupt();
    const id = crypto.randomUUID();
    await bindUserTurn(id);
    if (!pcRef.current) await start();
    upsert(id, 'user', (m) => ({ ...m, live: false, cards: [{ kind: 'image', url: dataUrl, title: 'Photo', subtitle: 'Added from your computer' }] }));
    // The saved conversation notes the photo; the pixels are not stored in the timeline.
    await persist('user', 'Shared a photo', undefined, id);
    for (let i = 0; i < 50 && channelRef.current?.readyState !== 'open'; i++) await new Promise((r) => setTimeout(r, 200));
    const channel = channelRef.current;
    if (!channel || channel.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_image', image_url: dataUrl }] } }));
    void requestResponse(channel);
  }, [upsert, persist, start, interrupt, requestResponse, bindUserTurn]);

  /**
   * The shortcut (Command twice; see DaimonPresence). It continues the conversation open in this
   * tab, so she keeps what was said and done; with none open, it starts one. Pressed during a call,
   * it hangs up. The user stays where they are: she docks there.
   */
  const allowed = useKernelStore((s) => canAccessDaimon(s.userContext));
  const summon = useCallback(async () => {
    if (hostedEnabled) return;
    if (pcRef.current) { stop(); return; }
    if (!allowed) return;
    await followSubject();
    const active = document.querySelector<HTMLElement>('[id^="portal-nav-"][aria-current="page"]')?.innerText.trim();
    const place = active && active !== 'Daimon' ? `the ${active} module, showing: ${readScreen(300)}` : 'your page';
    void start(`[The user called you with the keyboard shortcut while on ${place}. Greet them in a few words and ask what they need there.]`);
  }, [start, stop, allowed, followSubject]);

  /** Shows her an image without asking for an answer: what the user's camera sees, during a face call. */
  /** Silences her direct voice while the face plays it in sync with her lips. Volume, not mute: the
   * interruption code toggles mute on its own. */
  const setVoiceSilent = useCallback((silent: boolean) => { if (audioRef.current) audioRef.current.volume = silent ? 0 : 1; }, []);
  const glance = useCallback((dataUrl: string) => {
    const channel = channelRef.current;
    if (channel?.readyState !== 'open') return;
    channel.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [
      { type: 'input_text', text: '[What my camera sees right now. Do not describe it unless it matters or I ask.]' },
      { type: 'input_image', image_url: dataUrl },
    ] } }));
  }, []);

  const toggleMute = useCallback(() => {
    const next = !muted;
    micRef.current?.getAudioTracks().forEach((track) => { track.enabled = !next; });
    setMuted(next);
  }, [muted]);

  const openSavedConversation = useCallback((id: string) => selectConversation({ action: 'select', sessionId: id }, { stop: true }), [selectConversation]);
  const newConversation = useCallback(async () => {
    if (hostedEnabled) return true; // Opening the hosted page is not a user's new-conversation intent.
    if (returningRef.current) { returningRef.current = false; return true; }
    const selected = await selectConversation({ action: 'create', explicitNew: true }, { stop: true });
    return Boolean(selected.id);
  }, [selectConversation]);

  return {
    openSavedConversation, newConversation,
    state, error, muted, microphoneEnabled, level, working, browsing, messages, start, stop, toggleMute, sendText, sendImage,
    // Working (a tool, a search) is thinking too; outside a call there is no phase.
    activity: (state !== 'live' ? null : phase === 'speaking' ? 'speaking' : working || browsing !== null ? 'thinking' : phase) as DaimonActivity,
    pointer, driving, takeOver, docked: stages === 0 || settingsOpen, registerStage, returnToConversation, summon, approval, answerApproval, screenAsk, answerScreenAsk, sharing, toggleScreenShare,
    voiceStream, setVoiceSilent, glance,
  };
}

export type Daimon = ReturnType<typeof useDaimon>;
const DaimonContext = createContext<Daimon | null>(null);

/**
 * One Daimon for the whole app, above every page: her call stays on while she opens a module for
 * the user or the user walks elsewhere, and her cursor, frame and docked orb draw over any screen.
 */
export const DaimonProvider = ({ children }: { children: React.ReactNode }) => {
  const daimon = useDaimon();
  return (
    <DaimonContext.Provider value={daimon}>
      {children}
      <DaimonPresence daimon={daimon} />
      <DaimonFace daimon={daimon} />
    </DaimonContext.Provider>
  );
};

export const useDaimonContext = () => {
  const daimon = useContext(DaimonContext);
  if (!daimon) throw new Error('DaimonStage needs DaimonProvider above it.');
  return daimon;
};

const DAIMON_MODES = ['MANUAL', 'ACCEPT_EDITS', 'AUTO'] as const;
const DAIMON_MODE_LABELS: Record<'en' | 'fr', Record<DaimonAgentMode, string>> = {
  en: { MANUAL: 'Manual', ACCEPT_EDITS: 'Accept edits', AUTO: 'Auto' },
  fr: { MANUAL: 'Manuel', ACCEPT_EDITS: 'Accepter les modifications', AUTO: 'Auto' },
};

/** Her mode picker: Source's chip, remembered per user. */
const DaimonModeChip = () => {
  const { kernel } = useKernel();
  const stored = useKernelStore((s) => s.userPreferences.daimonAgentMode);
  const lang = useKernelStore((s) => (s.language === 'fr' ? 'fr' : 'en'));
  const onSelect = useCallback((next: DaimonAgentMode) => {
    void kernel.current?.updateAndSaveUserPreference('daimonAgentMode', next);
    kernel.current?.logUserAction?.('Daimon Mode Changed', { mode: next });
  }, [kernel]);
  return <ModeChip modes={DAIMON_MODES} mode={daimonMode(stored)} labels={DAIMON_MODE_LABELS[lang]} header="Mode" onSelect={onSelect} pill />;
};

/** A picked photo as a JPEG data URL, at most 1024 px on its long side, so it stays small in transit. */
const readPhoto = (file: File) => new Promise<string>((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not an image.')); };
  img.src = url;
});

/** Each card fades up softly as it arrives; still for anyone who asked for reduced motion. */
const APPEAR = 'animate-in fade-in-0 zoom-in-[0.97] slide-in-from-bottom-1 duration-500 ease-out fill-mode-both motion-reduce:animate-none';

/** Images as ChatGPT shows them: a grid of rounded thumbnails, the project under each. */
const ImageCards = ({ cards, onOpen }: { cards: DaimonCard[]; onOpen: (card: DaimonCard) => void }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
    {cards.map((card) => (
      <button key={card.url} type="button" onClick={() => onOpen(card)} className={`group text-left ${APPEAR}`}>
        <img src={card.url} alt={card.title} loading="lazy" className="aspect-square w-full rounded-[16px] bg-[#212121] object-cover transition-opacity group-hover:opacity-90" />
        <span className="mt-1.5 block truncate text-[#AFAFAF]" style={{ fontSize: 12 }}>{card.title} · {card.subtitle}</span>
      </button>
    ))}
  </div>
);

/** Files as ChatGPT attaches them: a rounded card, the file mark, the name and what it is. */
const DocumentCards = ({ cards, onOpen }: { cards: DaimonCard[]; onOpen: (card: DaimonCard) => void }) => (
  <div className="flex flex-col gap-2">
    {cards.map((card) => (
      <button
        key={card.url}
        type="button"
        onClick={() => onOpen(card)}
        className={`flex w-full max-w-[420px] items-center gap-3 rounded-[16px] border border-[#262626] bg-[#1B1B1B] px-3 py-2.5 text-left transition-colors hover:bg-[#262626] ${APPEAR}`}
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-white ${card.kind === 'link' ? 'bg-[#2F5BD3]' : 'bg-[#E0533D]'}`}>
          {card.kind === 'link' ? <Globe size={20} /> : <FileText size={20} />}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-medium text-white" style={{ fontSize: 14 }}>{card.title}</span>
          <span className="block truncate text-[#AFAFAF]" style={{ fontSize: 12 }}>{card.subtitle}</span>
        </span>
      </button>
    ))}
  </div>
);

/**
 * FAKE STREAMING (owner, 2026-09-28). The text arrives long before it is said: the Daimon's
 * transcript lands in bursts while she takes seconds to speak it, the user's lands whole once they
 * stop. So words are revealed at a pace: the Daimon's at speaking speed, the user's quickly, as
 * ChatGPT types them out. Messages loaded from a saved conversation show whole.
 */
function useReveal(text: string, charsPerSecond: number, live: boolean | undefined) {
  const [shown, setShown] = useState(live ? 0 : text.length);
  const caughtUp = shown >= text.length;
  useEffect(() => {
    if (!live || caughtUp) return;
    const step = Math.max(1, Math.round(charsPerSecond * 0.04));
    const id = setInterval(() => setShown((n) => Math.min(text.length, n + step)), 40);
    return () => clearInterval(id);
  }, [live, caughtUp, text.length, charsPerSecond]);
  return live ? text.slice(0, shown) : text;
}

const UserBubble = ({ message }: { message: DaimonMessage }) => {
  const text = useReveal(message.text, 45, message.live);
  return (
    <div className="ml-auto max-w-[75%] rounded-[22px] bg-[#1F3B74] px-4 py-2.5 text-white" style={{ fontSize: 16, lineHeight: '24px' }}>
      {text || '…'}
    </div>
  );
};

/** Characters of her speech per card: the next card shows as she moves on to it. */
const CHARS_PER_CARD = 14;

/**
 * One answer of the Daimon, streamed as ChatGPT streams (owner, 2026-09-28: 'an intelligent stream
 * even when the daimon shows images, pdfs'): her words at the pace of her voice (about 15 characters
 * a second), then what she found below them, one card at a time as her words advance, all of it
 * once she is done.
 */
const DaimonAnswer = ({ message, onOpen }: { message: DaimonMessage; onOpen: (card: DaimonCard) => void }) => {
  const text = useReveal(message.text, 15, message.live);
  const lang = useKernelStore((s) => (s.language === 'fr' ? 'fr' : 'en'));
  const all = message.cards ?? [];
  // A live view shows as soon as she starts on it (show_visual): it is what she is talking about.
  const views = all.filter((card) => card.kind === 'visual');
  const cards = all.filter((card) => card.kind !== 'visual');
  const finished = !message.live || (message.done && text.length === message.text.length);
  const visible = finished ? cards : cards.slice(0, Math.floor(text.length / CHARS_PER_CARD));
  return (
    <div className="flex flex-col gap-3">
      {views.map((card) => <DaimonVisualCard key={`${card.brandId}-${card.visual}`} card={card} lang={lang} />)}
      {text ? <p className="text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '28px' }}>{text}</p> : null}
      {visible.length ? (
        visible[0]!.kind === 'image'
          ? <ImageCards cards={visible} onOpen={onOpen} />
          : <DocumentCards cards={visible} onOpen={onOpen} />
      ) : null}
      {text && finished ? <div className="-ml-1.5 -mt-2 flex items-center"><CopyAction text={message.text} /></div> : null}
    </div>
  );
};

/** ChatGPT's copy action under a reply. */
const CopyAction = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <AppTooltip content={copied ? 'Copied' : 'Copy'}>
      <button
        type="button"
        aria-label="Copy"
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
        }}
        className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </AppTooltip>
  );
};

/**
 * The Daimon's page, ChatGPT's voice-in-chat screen (owner, 2026-09-28): the thread in the
 * middle (the user's words as blue bubbles, the Daimon's as text with a copy action, a 'Working'
 * line while it fetches), the bubble floating above the composer while a call is live, and the
 * composer at the bottom: type to write; the blue button starts talking; mute and end replace it
 * during a call. With nothing said yet, the bubble alone sits in the middle.
 */
export const DaimonStage = () => {
  const { kernel } = useKernel();
  const daimon = useDaimonContext();
  const { registerStage } = daimon;
  useEffect(registerStage, [registerStage]);
  const live = daimon.state === 'live';
  const busy = daimon.state === 'connecting' || live;
  const thread = daimon.messages.filter((m) => m.text || m.cards?.length);
  const [draft, setDraft] = useState('');
  // Face to face (owner, 2026-10-02): her face on top of the voice call, the way ChatGPT, Gemini
  // Live and FaceTime add video to a call in place; it starts the call when none is running.
  const face = useDaimonFace();
  const toggleFace = useCallback(() => {
    face.set({ on: !face.on, camera: false });
    if (!face.on && daimon.state !== 'live' && daimon.state !== 'connecting') void daimon.start();
  }, [face, daimon]);
  const videoAllowed = useKernelStore((state) => canUseDaimonVideo(state.userContext));
  const videoButton = !videoAllowed ? null : (
    <AppTooltip content={face.on ? 'Turn off video' : 'Video'}>
      <button type="button" onClick={toggleFace} aria-pressed={face.on} aria-label="Talk face to face" className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${face.on ? 'bg-[#ECECEC] text-[#0D0D0D]' : 'text-[#E5E5E5] hover:bg-white/[0.08]'}`}>
        <Video size={19} strokeWidth={2} />
      </button>
    </AppTooltip>
  );
  const composerRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [dictationLanguage, setDictationLanguage] = useState('en-US');
  // The mic dictates into the box (ChatGPT's mic); the blue button is the live voice conversation.
  const handleDictation = useCallback((transcript: string, isFinal: boolean) => {
    const words = transcript.trim();
    if (isFinal && words) setDraft((prev) => (prev.trim() ? `${prev.trimEnd()} ${words}` : words));
  }, []);
  // ChatGPT's + : the composer-wide drop-up the other chat boxes use.
  const openPlusMenu = useCallback(() => {
    const rect = composerRef.current?.getBoundingClientRect();
    if (!rect) return;
    kernel.current?.setMenuData({
      position: { x: rect.left, y: rect.top - 8 },
      actions: [
        { label: 'Add photos & files', hint: 'Upload from computer', icon: <Paperclip size={18} className="text-white/90" />, onClick: () => fileRef.current?.click() },
        daimon.sharing
          ? { label: 'Stop sharing screen', hint: SHARE_SHORTCUT, icon: <MonitorOff size={18} className="text-white/90" />, onClick: () => void daimon.toggleScreenShare() }
          : { label: 'Share screen', hint: `Let the Daimon see what you see · ${SHARE_SHORTCUT}`, icon: <MonitorUp size={18} className="text-white/90" />, onClick: () => void daimon.toggleScreenShare() },
      ],
      skin: 'composer',
      width: rect.width,
    });
  }, [kernel, daimon.sharing, daimon.toggleScreenShare]);
  // FOLLOWING THE STREAM. Her words are revealed at speaking pace after the text has arrived, and
  // cards and the small orb change the height on their own, so a scroll on new text alone stopped
  // following her mid-sentence. The thread stays pinned to the bottom whenever its content or the
  // viewport grows, unless the user has scrolled up to read.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const hasThread = thread.length > 0;
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;
    const follow = () => { if (pinnedRef.current) scroller.scrollTop = scroller.scrollHeight; };
    const onScroll = () => { pinnedRef.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80; };
    const observer = new ResizeObserver(follow);
    observer.observe(content);
    observer.observe(scroller);
    scroller.addEventListener('scroll', onScroll, { passive: true });
    follow();
    return () => { observer.disconnect(); scroller.removeEventListener('scroll', onScroll); };
  }, [hasThread]);
  // Sending something always brings the latest into view, even after reading further up.
  const lastUserId = [...thread].reverse().find((m) => m.role === 'user')?.id;
  useEffect(() => {
    pinnedRef.current = true;
    const scroller = scrollRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [lastUserId]);
  const open = useCallback((card: DaimonCard) => {
    // A web page opens in its own tab: most sites refuse to be framed in the preview.
    if (card.kind === 'link') { window.open(card.url, '_blank', 'noopener,noreferrer'); return; }
    kernel.current?.openModal('DocumentPreview', { url: card.url, title: card.title });
  }, [kernel]);
  const submit = useCallback(() => {
    const text = draft;
    setDraft('');
    void daimon.sendText(text);
  }, [draft, daimon]);

  const composer = (
    <div ref={composerRef} className="w-full max-w-[768px] rounded-[28px] border border-[#262626] bg-[#1B1B1B] px-2 pb-2 pt-0 text-white">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void readPhoto(file).then(daimon.sendImage).catch(() => undefined);
        }}
      />
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit(); }
        }}
        rows={1}
        placeholder="Type"
        aria-label="Message the Daimon"
        data-daimon-tour="input"
        className="block w-full resize-none bg-transparent px-2.5 pb-2 pt-4 text-white placeholder:text-[#8E8E8E] focus:outline-none"
        style={{ fontSize: 16, lineHeight: '24px' }}
      />
      <div className="flex items-center justify-end gap-1">
        <AppTooltip content="Add photos & files">
          <button type="button" onClick={openPlusMenu} aria-label="Add photos and files" data-daimon-tour="plus" className="flex h-9 w-9 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08]">
            <Plus size={20} strokeWidth={2} />
          </button>
        </AppTooltip>
        <div className="mr-auto" data-daimon-tour="mode"><DaimonModeChip /></div>
        {daimon.state === 'error' ? <span className="mr-2 text-[#E88C89]" style={{ fontSize: 13 }}>{daimon.error}</span> : null}
        {live ? null : (
          <VoiceInputButton
            onTranscriptUpdate={handleDictation}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] disabled:opacity-50"
            iconSize={18}
            iconStrokeWidth={2}
            iconColor="currentColor"
            currentLanguage={dictationLanguage}
            onSetLanguage={setDictationLanguage}
          />
        )}
        {draft.trim() ? (
          <AppTooltip content="Send">
            <button type="button" onClick={submit} aria-label="Send" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3862C2] text-white transition-colors hover:bg-[#4270D6]">
              <ArrowUp size={19} strokeWidth={2.6} />
            </button>
          </AppTooltip>
        ) : busy ? (
          <>
            {live ? videoButton : null}
            {live ? (
              <AppTooltip content={!daimon.microphoneEnabled ? 'Voice' : daimon.muted ? 'Unmute' : 'Mute'}>
                <button type="button" onClick={() => { if (daimon.microphoneEnabled) daimon.toggleMute(); else void daimon.start(); }} aria-label={!daimon.microphoneEnabled ? 'Talk to the Daimon by voice' : daimon.muted ? 'Unmute' : 'Mute'} className="flex h-9 w-9 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08]">
                  {daimon.muted ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
              </AppTooltip>
            ) : null}
            <AppTooltip content="End">
              <button type="button" onClick={daimon.stop} aria-label="End the conversation" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3862C2] text-white transition-colors hover:bg-[#4270D6]">
                <X size={18} strokeWidth={2.4} />
              </button>
            </AppTooltip>
          </>
        ) : (
          <>
          {videoButton}
          <AppTooltip content="Voice">
            <button type="button" onClick={() => void daimon.start()} aria-label="Talk to the Daimon by voice" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3862C2] text-white transition-colors hover:bg-[#4270D6]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
              </svg>
            </button>
          </AppTooltip>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-full w-full flex-col" role="region" aria-label="Daimon">
      {thread.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
          <button
            type="button"
            onClick={() => void daimon.start()}
            disabled={busy}
            aria-label="Talk to the Daimon"
            data-daimon-tour="orb"
            className="rounded-full transition-transform duration-200 hover:scale-[1.03] disabled:hover:scale-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#3862C2]"
            data-daimon-orb
          >
            <span className="block" style={activityOpacity(daimon.activity)}><DaimonOrb size={168} level={daimon.level}><ActivityRing activity={daimon.activity} browsing={daimon.browsing !== null} /></DaimonOrb></span>
          </button>
          <p className="text-[#8E8E8E]" style={{ fontSize: 15 }} aria-live="polite">
            {daimon.state === 'connecting' ? 'Connecting…' : live ? (daimon.muted ? 'Muted' : `${ACTIVITY_LABEL[daimon.activity ?? 'waiting']}${daimon.activity === 'listening' || daimon.activity === 'thinking' ? '…' : ''}`) : `Tap to talk, or press ${SUMMON_LABEL} anywhere`}
          </p>
          {busy ? null : <DaimonTourButton />}
        </div>
      ) : (
        <div ref={scrollRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
          <div ref={contentRef} className="mx-auto flex w-full max-w-[768px] flex-col gap-7 px-2 pb-6 pt-10">
            {thread.map((m) =>
              m.role === 'user' ? (
                m.cards?.length ? (
                  <img key={m.id} src={m.cards[0]!.url} alt="Your photo" className={`ml-auto max-h-[260px] max-w-[60%] rounded-[18px] object-cover ${APPEAR}`} />
                ) : <UserBubble key={m.id} message={m} />
              ) : (
                <DaimonAnswer key={m.id} message={m} onOpen={open} />
              ),
            )}
            {daimon.browsing !== null ? (
              <p className="flex items-center gap-2 text-[#8E8E8E]" style={{ fontSize: 16 }}>
                <Globe size={17} className="shrink-0" />
                <span className="thinking-text-animated" style={{ fontSize: 16 }}>{daimon.browsing ? `Browsing the web for “${daimon.browsing}”` : 'Browsing the web'}</span>
              </p>
            ) : daimon.working ? <p className="thinking-text-animated text-[#8E8E8E]" style={{ fontSize: 16 }}>Working</p> : null}
          </div>
        </div>
      )}
      <DaimonTour summon={SUMMON_LABEL} share={SHARE_SHORTCUT} />
      <div className="flex shrink-0 flex-col items-center gap-4 px-2 pb-5 pt-2">
        {thread.length > 0 && busy ? <div data-daimon-orb data-activity={daimon.activity ?? undefined} className="relative" title={daimon.activity ? ACTIVITY_LABEL[daimon.activity] : undefined}><span className="block" style={activityOpacity(daimon.activity)}><DaimonOrb size={88} level={daimon.level}><ActivityRing activity={daimon.activity} browsing={daimon.browsing !== null} />{daimon.browsing !== null ? <BrowsingRing /> : null}</DaimonOrb></span></div> : null}
        {composer}
      </div>
    </div>
  );
};
